// Enhanced security imports with integrity validation
import { PDFDocument, PDFForm, PDFTextField, rgb, StandardFonts } from 'pdf-lib';
import * as fontkit from '@pdf-lib/fontkit';
import './fonts.css';
import './Personalise.css';
import './Modal.css';
import './OrderForm.css';
import './Legacy.css';
import './Thanks.css';

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  Image as ImageIcon,
  Minus,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  X
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';  
import './App.css';

// SECURITY: Enhanced input sanitization with NIST SP 800-53 compliance
const sanitizeInput = (input) => {
  if (!input) return '';
  
  // Enhanced validation with proper type checking and length limits
  const str = String(input).slice(0, 100);
  
  // OWASP compliant character escaping with comprehensive coverage
  const escapeMap = {
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '&': '&amp;',
    '/': '&#x2F;',
    '\\': '&#x5C;',
    '`': '&#x60;',
    '=': '&#x3D;'
  };
  
  return str
    .replace(/[<>"'&/\\`=]/g, (match) => escapeMap[match] || match)
    // Enhanced script and protocol detection
    .replace(/javascript\s*:/gi, 'blocked:')
    .replace(/data\s*:/gi, 'blocked:')
    .replace(/vbscript\s*:/gi, 'blocked:')
    .replace(/on\w+\s*=/gi, 'blocked=')
    .replace(/<\s*script[^>]*>/gi, '&lt;blocked&gt;')
    .replace(/<\s*\/\s*script\s*>/gi, '&lt;/blocked&gt;')
    .replace(/expression\s*\(/gi, 'blocked(')
    .replace(/url\s*\(/gi, 'blocked(')
    .trim();
};

// SECURITY: Enhanced validation with allowlisting approach (NIST SI-10)
const validateInput = (input, type = 'text') => {
  if (!input) return '';
  
  switch (type) {
    case 'phone':
      // Strict phone validation - only digits, limit to 10
      const phoneDigits = input.replace(/[^\d]/g, '').slice(0, 10);
      return phoneDigits.match(/^\d{10}$/) ? phoneDigits : phoneDigits.slice(0, 10);
      
    case 'email':
      // Enhanced email validation with domain restrictions
      const email = input.toLowerCase().trim().slice(0, 254); // RFC 5321 limit
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) return '';
      // Additional domain security checks
      const domain = email.split('@')[1];
      if (domain && (domain.includes('..') || domain.startsWith('.') || domain.endsWith('.'))) {
        return '';
      }
      return sanitizeInput(email);
      
    case 'vehicleModel':
      // Allowlist validation for vehicle models
      const allowedModels = ['Hector Hawk'];
      return allowedModels.includes(input) ? input : '';
      
    case 'accessory':
      // Allowlist validation for accessories
      const allowedAccessories = ['Cushion Pillow Memory', 'Cockpit New Pillow', 'Passenger NeckRest'];
      return allowedAccessories.includes(input) ? input : '';
      
    case 'font':
      // Allowlist validation for fonts
      const allowedFonts = ['Book Script', 'Brushability', 'Chancery', 'Berlinsans', 'Georgia'];
      return allowedFonts.includes(input) ? input : '';
      
    case 'color':
      // Allowlist validation for colors
      const allowedColors = ['#005d8f', '#000000', '#d10000', '#ffe599', '#c0c0c0'];
      return allowedColors.includes(input) ? input : allowedColors[0];
      
    case 'personalised':
      // Enhanced personalised text validation
      const text = sanitizeInput(input).slice(0, 7);
      // Only allow alphanumeric and basic punctuation
      return text.replace(/[^a-zA-Z0-9\s\-_.]/g, '');
      
    case 'number':
      // Strict number validation
      const num = parseInt(input);
      return (!isNaN(num) && num > 0 && num <= 100) ? num : 1;
      
    default:
      return sanitizeInput(input).slice(0, 100);
  }
};

// SECURITY: Enhanced email validation with comprehensive checks
const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  // RFC 5322 compliant regex with additional security checks
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(email)) return false;
  
  // Additional security checks
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  
  const [localPart, domain] = parts;
  
  // Local part validation
  if (localPart.length > 64 || localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
    return false;
  }
  
  // Domain validation
  if (domain.length > 253 || domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) {
    return false;
  }
  
  return true;
};

// SECURITY: Enhanced URL validation with strict allowlisting
const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const urlObj = new URL(url);
    
    // Only allow HTTPS for external resources (PCI DSS compliance)
    if (!['https:'].includes(urlObj.protocol)) return false;
    
    // Domain allowlisting for external resources
    const allowedDomains = [
      'fonts.googleapis.com',
      'fonts.gstatic.com',
      'cdnjs.cloudflare.com'
    ];
    
    return allowedDomains.some(domain => urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain));
  } catch {
    return false;
  }
};

// SECURITY: Enhanced phone validation
const isValidPhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  const cleanPhone = phone.replace(/[^\d]/g, '');
  return /^[6-9]\d{9}$/.test(cleanPhone); // Indian mobile number format
};

// SECURITY: Safe DOM manipulation with comprehensive validation
const safeSetTextContent = (element, text) => {
  if (!element || typeof element.textContent === 'undefined') {
    throw new Error('Invalid element for text content');
  }
  
  // Validate element is still in DOM and not compromised
  if (!document.contains(element)) {
    throw new Error('Element not in document');
  }
  
  const sanitizedText = sanitizeInput(text);
  element.textContent = sanitizedText;
  
  // Verify the content was set correctly
  if (element.textContent !== sanitizedText) {
    throw new Error('Text content validation failed');
  }
};

// SECURITY: Enhanced DOM manipulation with strict validation
const safeSetInnerHTML = (element, html) => {
  if (!element || !document.contains(element)) {
    throw new Error('Invalid element for innerHTML');
  }
  
  // Strip all HTML and use textContent instead for maximum security
  const sanitized = sanitizeInput(html).replace(/<[^>]*>/g, '');
  element.textContent = sanitized;
};

// SECURITY: Enhanced node validation with comprehensive checks
const safeInsertBefore = (newNode, referenceNode) => {
  if (!newNode || !referenceNode || !referenceNode.parentNode) {
    throw new Error('Invalid nodes for insertion');
  }
  
  if (!document.contains(referenceNode)) {
    throw new Error('Reference node not in document');
  }
  
  const allowedNodeTypes = [Node.TEXT_NODE, Node.ELEMENT_NODE];
  if (!allowedNodeTypes.includes(newNode.nodeType)) {
    throw new Error('Invalid node type for insertion');
  }
  
  if (newNode.nodeType === Node.ELEMENT_NODE) {
    sanitizeElement(newNode);
  }
  
  return referenceNode.parentNode.insertBefore(newNode, referenceNode);
};

// SECURITY: Comprehensive element sanitization
const sanitizeElement = (element) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return;
  
  // Remove all dangerous attributes
  const dangerousAttrs = [
    'onclick', 'onload', 'onerror', 'onmouseover', 'onmouseout',
    'onfocus', 'onblur', 'onchange', 'onsubmit', 'onreset',
    'onselect', 'onkeydown', 'onkeypress', 'onkeyup', 'onscroll',
    'javascript', 'vbscript', 'data', 'src', 'href', 'action',
    'formaction', 'background', 'style'
  ];
  
  // Remove attributes using a more thorough approach
  for (let i = element.attributes.length - 1; i >= 0; i--) {
    const attr = element.attributes[i];
    if (dangerousAttrs.some(dangerous => attr.name.toLowerCase().includes(dangerous.toLowerCase()))) {
      element.removeAttribute(attr.name);
    }
  }
  
  // Sanitize text content
  if (element.textContent) {
    element.textContent = sanitizeInput(element.textContent);
  }
  
  // Recursively sanitize children
  Array.from(element.children).forEach(child => sanitizeElement(child));
};

// SECURITY: Enhanced node validation with stricter checks
const isValidNode = (node) => {
  if (!node) return false;
  
  const allowedTypes = [Node.TEXT_NODE, Node.ELEMENT_NODE];
  if (!allowedTypes.includes(node.nodeType)) return false;
  
  if (node.nodeType === Node.ELEMENT_NODE) {
    const allowedTags = ['DIV', 'SPAN', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BR', 'HR'];
    if (!allowedTags.includes(node.tagName.toUpperCase())) return false;
    
    // Check for dangerous attributes
    const dangerousAttrs = ['onclick', 'onerror', 'onload', 'javascript:', 'data:', 'vbscript:'];
    for (let attr of node.attributes || []) {
      if (dangerousAttrs.some(dangerous => attr.value?.toLowerCase().includes(dangerous))) {
        return false;
      }
    }
  }
  
  return true;
};

// SECURITY: Enhanced styles with CSP compliance
const extraStyles = `
  .embroidered-text {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  @keyframes toastIn {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .toast-card { animation: toastIn 280ms cubic-bezier(.21,.61,.35,1); }
  .toast-close { transition: opacity .2s ease, transform .2s ease; }
  .toast-close:hover { opacity: .85; transform: scale(1.05); }
`;

// SECURITY: Allowlisted data with validation
const vehicleModels = ['Hector Hawk'];
const accessories = ['Cushion Pillow Memory', 'Cockpit New Pillow', 'Passenger NeckRest'];
const accessorySeatViews = Object.freeze({
  'Cushion Pillow Memory': Object.freeze(['Front Row', 'Rear Row']),
  'Cockpit New Pillow': Object.freeze(['Front Row', 'Rear Row']),
  'Passenger NeckRest': Object.freeze(['Front Row', 'Rear Row'])
});
const fontStyles = ['Book Script', 'Brushability', 'Chancery', 'Berlinsans', 'Georgia'];
const textColors = [
  { name: 'Blue', value: '#005d8f' },
  { name: 'Black', value: '#000000' },
  { name: 'Red', value: '#d10000' },
  { name: 'Beige', value: '#ffe599' },
  { name: 'Silver', value: '#c0c0c0' }
];

const designOptions = Object.freeze([
  Object.freeze({ id: 'superman', name: 'Superman', code: 'S', src: '/designs/superman-removebg-preview.png' }),
  Object.freeze({ id: 'gorkha', name: 'Gorkha', code: 'G', src: '/designs/gorkha-removebg-preview.png' }),
  Object.freeze({ id: 'batman', name: 'Batman', code: 'B', src: '/designs/batman-removebg-preview.png?v=2' }),
  Object.freeze({ id: 'mountain', name: 'Mountain', code: 'M', src: '/designs/mountain.png?v=2' }),
  Object.freeze({ id: 'para-sf', name: 'Para SF', code: 'PS', src: '/designs/parasf.png?v=2', scale: 0.9 }),
  Object.freeze({
    id: 'balidan',
    name: 'Balidan',
    code: 'BL',
    src: '/designs/balidan.png?v=2',
    scale: 0.9
  })
]);

const CUSTOM_DESIGN_PREFIX = 'custom-';
const CUSTOM_DESIGN_STORAGE_KEY = 'mgp.customDesigns.v2';
const CUSTOM_DESIGN_MAX_SLOTS = 6;
/* Matched to the built-in artwork (see public/designs): content is kept at
   its natural aspect ratio on a canvas whose longest edge is ~1076px, with
   a symmetric transparent margin of ~2.2% of that edge. Balidan is
   1076x758 with 1028x710 of content and a 24px margin on all four sides;
   squaring uploads instead would letterbox wide logos and render them
   smaller than the built-ins at the same placement size. */
const CUSTOM_DESIGN_MAX_EDGE = 1076;
const CUSTOM_DESIGN_MARGIN_RATIO = 24 / 1076;
const CUSTOM_DESIGN_MAX_BYTES = 8 * 1024 * 1024;
const CUSTOM_DESIGN_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/bmp,image/svg+xml';

const isCustomDesignId = (id) => typeof id === 'string' && id.startsWith(CUSTOM_DESIGN_PREFIX);

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('Could not read that file.'));
  reader.readAsDataURL(file);
});

const loadImageElement = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('That image could not be decoded.'));
  image.src = src;
});

/* Flood fill inward from the border pixels, clearing anything within
   `tolerance` of the sampled background. Works from the edges rather than
   by colour match alone, so a dark shape on a dark background survives as
   long as it does not touch the frame. */
/* Trim a transparent-background canvas to its artwork and re-pad it to the
   built-in convention: natural aspect ratio, longest edge filling the canvas
   minus a symmetric margin. Shared by both removal paths so uploads always
   sit in the placement box the way the shipped designs do. */
const normaliseToDesignCanvas = (source) => {
  const width = source.width;
  const height = source.height;
  const context = source.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Canvas is unavailable in this browser.');
  const pixels = context.getImageData(0, 0, width, height).data;

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (pixels[(y * width + x) * 4 + 3] <= 8) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX || maxY < minY) {
    throw new Error('The whole image was treated as background. Try a picture with a clearer subject.');
  }

  const cropWidth = maxX - minX + 1;
  const cropHeight = maxY - minY + 1;

  const margin = Math.round(CUSTOM_DESIGN_MAX_EDGE * CUSTOM_DESIGN_MARGIN_RATIO);
  const contentMax = CUSTOM_DESIGN_MAX_EDGE - margin * 2;
  const contentScale = contentMax / Math.max(cropWidth, cropHeight);
  const contentWidth = Math.max(1, Math.round(cropWidth * contentScale));
  const contentHeight = Math.max(1, Math.round(cropHeight * contentScale));

  const output = document.createElement('canvas');
  output.width = contentWidth + margin * 2;
  output.height = contentHeight + margin * 2;
  const outputContext = output.getContext('2d');
  if (!outputContext) throw new Error('Canvas is unavailable in this browser.');
  outputContext.imageSmoothingQuality = 'high';
  outputContext.drawImage(
    source,
    minX, minY, cropWidth, cropHeight,
    margin, margin, contentWidth, contentHeight,
  );

  return output.toDataURL('image/png');
};

/* Fallback only. Corner flood fill cannot clear regions enclosed by the
   artwork (the inside of a ring stays opaque) and leaves a fringe on
   anti-aliased edges, so it is used only when the model is unavailable. */
const removeBackgroundByFloodFill = (image, tolerance = 32) => {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) throw new Error('That image looks empty.');

  const scale = Math.min(1, CUSTOM_DESIGN_MAX_EDGE / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Canvas is unavailable in this browser.');
  context.drawImage(image, 0, 0, width, height);

  const imageData = context.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  // Average the four corners so a single noisy pixel cannot skew the key.
  const cornerOffsets = [
    0,
    (width - 1) * 4,
    (height - 1) * width * 4,
    ((height - 1) * width + (width - 1)) * 4,
  ];
  let keyR = 0;
  let keyG = 0;
  let keyB = 0;
  cornerOffsets.forEach(offset => {
    keyR += pixels[offset];
    keyG += pixels[offset + 1];
    keyB += pixels[offset + 2];
  });
  keyR /= cornerOffsets.length;
  keyG /= cornerOffsets.length;
  keyB /= cornerOffsets.length;

  const toleranceSq = tolerance * tolerance * 3;
  const visited = new Uint8Array(width * height);
  const stack = [];

  for (let x = 0; x < width; x += 1) {
    stack.push(x, x + (height - 1) * width);
  }
  for (let y = 0; y < height; y += 1) {
    stack.push(y * width, y * width + (width - 1));
  }

  while (stack.length) {
    const index = stack.pop();
    if (index < 0 || index >= visited.length || visited[index]) continue;

    const offset = index * 4;
    if (pixels[offset + 3] === 0) {
      visited[index] = 1;
      continue;
    }

    const dr = pixels[offset] - keyR;
    const dg = pixels[offset + 1] - keyG;
    const db = pixels[offset + 2] - keyB;
    if (dr * dr + dg * dg + db * db > toleranceSq) continue;

    visited[index] = 1;
    pixels[offset + 3] = 0;

    const x = index % width;
    if (x > 0) stack.push(index - 1);
    if (x < width - 1) stack.push(index + 1);
    stack.push(index - width, index + width);
  }

  context.putImageData(imageData, 0, 0);

  return normaliseToDesignCanvas(canvas);
};

/* Segmentation model, loaded on demand so the ~5MB of weights are only
   fetched when someone actually uploads artwork. Handles enclosed regions
   and soft edges that the flood-fill fallback cannot. */
let backgroundRemovalModule = null;
const loadBackgroundRemovalModule = async () => {
  if (!backgroundRemovalModule) {
    backgroundRemovalModule = import('@imgly/background-removal');
  }
  return backgroundRemovalModule;
};

/* Let the browser paint before returning. Progress reports arrive
   synchronously right before long blocking work, so without this the label
   change never reaches the screen. */
const yieldToPaint = () => new Promise(resolve => {
  requestAnimationFrame(() => setTimeout(resolve, 0));
});

/* The library memoises its session on a stringified config, so the model is
   downloaded and compiled once per page load - but that happens inside the
   first removeBackground call, before any compute step is reported. Warming
   it separately means the long first wait is attributed to "preparing" and
   every later image starts straight at inference. */
/* Must be byte-identical between preload and removeBackground: the library
   memoises its session on JSON.stringify(config), so any difference would
   recompile the model instead of reusing it. Left on the default CPU device
   deliberately: `device: 'gpu'` loads a different WASM binary
   (ort-wasm-simd-threaded.jsep.wasm) that is not served here, which breaks
   session creation. */
const BACKGROUND_REMOVAL_CONFIG = Object.freeze({
  output: Object.freeze({ format: 'image/png' }),
});

let modelWarmPromise = null;
const warmBackgroundModel = async (onProgress) => {
  if (!modelWarmPromise) {
    modelWarmPromise = (async () => {
      const { preload } = await loadBackgroundRemovalModule();
      if (typeof preload !== 'function') return;
      await preload({
        ...BACKGROUND_REMOVAL_CONFIG,
        progress: (key, current, total) => {
          if (typeof onProgress !== 'function') return;
          const ratio = total > 0 ? current / total : 0;
          onProgress(String(key).startsWith('fetch') ? 'download' : 'warm', ratio);
        },
      });
    })().catch(error => {
      modelWarmPromise = null;
      throw error;
    });
  }
  return modelWarmPromise;
};

/* The model always infers at 1024x1024 and we only keep a 1076px canvas, so
   anything larger is wasted work: the library's bilinear resize is a plain JS
   loop on the main thread and runs twice (down for inference, back up for the
   mask), both scaling with the source pixel count. A 12MP phone photo spends
   well over 150ms there alone. Downscaling first - on the GPU-backed canvas
   drawImage path - makes that cost roughly constant regardless of input size.
   Aspect ratio is preserved, so this cannot distort the design. */
const CUSTOM_DESIGN_INPUT_MAX_EDGE = 1400;

const downscaleFileForModel = async (file) => {
  try {
    const bitmap = await createImageBitmap(file);
    const longestEdge = Math.max(bitmap.width, bitmap.height);
    if (longestEdge <= CUSTOM_DESIGN_INPUT_MAX_EDGE) {
      bitmap.close?.();
      return file;
    }

    const scale = CUSTOM_DESIGN_INPUT_MAX_EDGE / longestEdge;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    return blob || file;
  } catch (error) {
    // Any failure here is only a lost optimisation, never a lost upload.
    console.warn('Could not pre-downscale image, using original:', error);
    return file;
  }
};

const removeBackgroundWithModel = async (file, onProgress) => {
  const { removeBackground } = await loadBackgroundRemovalModule();

  /* The library's progress callback is synchronous and fires immediately
     before each blocking step, so a plain setState is batched and never
     painted. Reporting through a microtask lets React flush the step to the
     screen during the awaits inside removeBackground. */
  const report = (step, ratio) => {
    if (typeof onProgress !== 'function') return;
    Promise.resolve().then(() => onProgress(step, ratio));
  };

  /* Warm-up is only an optimisation - removeBackground initialises the
     session itself if this fails, so never let it break the upload. */
  try {
    await warmBackgroundModel(onProgress);
  } catch (warmError) {
    console.warn('Model warm-up failed, continuing:', warmError);
  }
  report('decode', null);
  await yieldToPaint();
  /* The library reports `fetch:*` continuously while downloading weights,
     but the compute phase emits only five discrete marks and the heavy one
     (`compute:inference`) fires immediately BEFORE inference blocks the main
     thread. So a percentage would sit frozen at 25% for the whole wait.
     Steps are reported by name instead, and each is given a paint frame so
     the label is on screen before the thread stalls. */
  const modelInput = await downscaleFileForModel(file);
  const blob = await removeBackground(modelInput, {
    ...BACKGROUND_REMOVAL_CONFIG,
    progress: (key, current, total) => {
      const rawKey = String(key);
      if (rawKey.startsWith('fetch')) {
        report('download', total > 0 ? current / total : 0);
      } else {
        report(rawKey.slice(rawKey.indexOf(':') + 1) || 'inference', null);
      }
    },
  });
  /* createImageBitmap decodes off the main thread, unlike an <img> load, and
     needs no object URL round-trip. */
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Canvas is unavailable in this browser.');
    context.drawImage(bitmap, 0, 0);
    return normaliseToDesignCanvas(canvas);
  } finally {
    bitmap.close?.();
  }
};

/* Prefer the model; fall back to flood fill if it cannot load or run, so an
   offline or restricted browser still gets a usable cut-out. */
const prepareCustomDesignSource = async (file, onProgress) => {
  try {
    return { src: await removeBackgroundWithModel(file, onProgress), usedFallback: false };
  } catch (modelError) {
    console.warn('Model background removal unavailable, using fallback:', modelError);
    const dataUrl = await readFileAsDataUrl(file);
    const image = await loadImageElement(dataUrl);
    return { src: removeBackgroundByFloodFill(image), usedFallback: true };
  }
};

const designPlacementScales = Object.freeze({
  'Rear Row': Object.freeze({
    'Cockpit New Pillow': 0.62,
    'Passenger NeckRest': 0.62
  })
});

const getDesignPlacementScale = (accessory, row) =>
  designPlacementScales[row]?.[accessory] || 1;

const rearDesignPositions = Object.freeze({
  'Cushion Pillow Memory': Object.freeze([
    Object.freeze({ top: '62%', left: '25.5%', rotation: 0 }),
    Object.freeze({ top: '62%', left: '72.5%', rotation: 0 })
  ]),
  'Cockpit New Pillow': Object.freeze([
    Object.freeze({ top: '26.5%', left: '30.75%', rotation: 0 }),
    Object.freeze({ top: '26.5%', left: '72%', rotation: 0 })
  ]),
  'Passenger NeckRest': Object.freeze([
    Object.freeze({ top: '21.48%', left: '30.51%', rotation: 0 }),
    Object.freeze({ top: '21.48%', left: '72.46%', rotation: 0 })
  ])
});

const getDesignPositions = (positionMap, model, row, accessory) => {
  if (row === 'Rear Row' && rearDesignPositions[accessory]) {
    return rearDesignPositions[accessory];
  }

  return positionMap[model]?.[row]?.[accessory] || [];
};

/* Prices are withheld from display for now. The real values below still
   drive the quantity maths and the PDF, so the placeholder is applied at
   render time only - remove it to show real prices again. */
const PRICE_PLACEHOLDER = '₹XXXX';

// SECURITY: Validated pricing data
const kitPrices = Object.freeze({
  'Cushion Pillow Memory': '₹4,990',
  'Cockpit New Pillow': '₹4,990',
  'Passenger NeckRest': '₹4,990'
});

// SECURITY: Immutable position configurations
const textPositions = Object.freeze({
  'Hector Hawk': {
    'Front Row': {
      'Cushion Pillow Memory': [
        Object.freeze({ top: '66%', left: '22%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } }),
        Object.freeze({ top: '66%', left: '74%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } })
      ],
      'Cockpit New Pillow': [
        Object.freeze({ top: '33%', left: '24%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '33%', left: '75%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ],
      'Passenger NeckRest': [
        Object.freeze({ top: '28%', left: '25%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '28%', left: '78%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ]
    },
    'Rear Row': {
      'Cushion Pillow Memory': [
        Object.freeze({ top: '61%', left: '25%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } }),
        Object.freeze({ top: '61%', left: '73%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } })
      ],
      'Cockpit New Pillow': [
        Object.freeze({ top: '26%', left: '31.5%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '26%', left: '71.5%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ],
      'Passenger NeckRest': [
        Object.freeze({ top: '22%', left: '31%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '22%', left: '72%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ]
    }
  }
});

const pdfTextPositions = Object.freeze({
  'Hector Hawk': {
    'Front Row': {
      'Cushion Pillow Memory': [
        Object.freeze({ top: '66%', left: '22%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } }),
        Object.freeze({ top: '66%', left: '74%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } })
      ],
      'Cockpit New Pillow': [
        Object.freeze({ top: '33%', left: '24%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '33%', left: '75%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ],
      'Passenger NeckRest': [
        Object.freeze({ top: '28%', left: '25%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '28%', left: '78%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ]
    },
    'Rear Row': {
      'Cushion Pillow Memory': [
        Object.freeze({ top: '61%', left: '25%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } }),
        Object.freeze({ top: '61%', left: '73%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } })
      ],
      'Cockpit New Pillow': [
        Object.freeze({ top: '26%', left: '31.5%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '26%', left: '71.5%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ],
      'Passenger NeckRest': [
        Object.freeze({ top: '22%', left: '31%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '22%', left: '72%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ]
    }
  }
});

const previewTextPositions = Object.freeze({
  'Hector Hawk': {
    'Front Row': {
      'Cushion Pillow Memory': [
        Object.freeze({ top: '66%', left: '22%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } }),
        Object.freeze({ top: '66%', left: '74%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } })
      ],
      'Cockpit New Pillow': [
        Object.freeze({ top: '33%', left: '24%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '33%', left: '75%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ],
      'Passenger NeckRest': [
        Object.freeze({ top: '28%', left: '25%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '28%', left: '78%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ]
    },
    'Rear Row': {
      'Cushion Pillow Memory': [
        Object.freeze({ top: '61%', left: '25%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } }),
        Object.freeze({ top: '61%', left: '73%', rotation: 0, fontSize: { desktop: 18, mobile: 8 } })
      ],
      'Cockpit New Pillow': [
        Object.freeze({ top: '26%', left: '31.5%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '26%', left: '71.5%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ],
      'Passenger NeckRest': [
        Object.freeze({ top: '22%', left: '31%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } }),
        Object.freeze({ top: '22%', left: '72%', rotation: 0, fontSize: { desktop: 16, mobile: 7 } })
      ]
    }
  }
});

// SECURITY: Enhanced font loading with integrity checks and timeouts
const loadFonts = async () => {
  const style = document.createElement('style');
  style.textContent = `
    @font-face {
      font-family: 'Dancing Script';
      src: local('Dancing Script'), 
           local('DancingScript-Regular');
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Montserrat';
      src: local('Montserrat'),
           local('Montserrat-Regular');
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Gabriola';
      src: local('Gabriola'), local('Segoe Script');
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Blackadder ITC';
      src: local('Blackadder ITC'), local('Brush Script MT');
      font-display: swap;
    }
    
    .font-fallback {
      font-family: 'Dancing Script', 'Times New Roman', serif;
    }
  `;
  
  // SECURITY: Enhanced external font loading with integrity validation
  const loadExternalFont = (url, expectedIntegrity = null) => {
    return new Promise((resolve) => {
      if (!isValidUrl(url)) {
        console.warn('Invalid font URL, using system fonts');
        resolve();
        return;
      }
      
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.crossOrigin = 'anonymous';
      
      // SECURITY: Add integrity check if provided
      if (expectedIntegrity) {
        link.integrity = expectedIntegrity;
      }
      
      let loaded = false;
      const timeout = 5000; // Reduced timeout for better UX
      
      const cleanup = () => {
        if (!loaded) {
          loaded = true;
          resolve();
        }
      };
      
      link.onload = cleanup;
      link.onerror = () => {
        console.warn(`Failed to load font from ${url}, using fallbacks`);
        cleanup();
      };
      
      // SECURITY: Set strict timeout
      const timeoutId = setTimeout(() => {
        if (!loaded) {
          console.warn(`Font loading timeout for ${url}`);
          if (link.parentNode) {
            link.parentNode.removeChild(link);
          }
          cleanup();
        }
      }, timeout);
      
      // Cleanup timeout on successful load
      link.addEventListener('load', () => clearTimeout(timeoutId));
      
      document.head.appendChild(link);
    });
  };
  
  document.head.appendChild(style);
  
  try {
    // SECURITY: Load fonts with race condition protection
    await Promise.race([
      loadExternalFont('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Montserrat:wght@400;700&display=swap'),
      new Promise(resolve => setTimeout(resolve, 5000)) // Strict timeout
    ]);
  } catch (error) {
    console.warn('External font loading failed, using system fonts');
  }

  try {
    await Promise.race([
      document.fonts.ready,
      new Promise(resolve => setTimeout(resolve, 3000))
    ]);
  } catch (error) {
    console.warn('Font ready timeout, proceeding with available fonts');
  }
};

// SECURITY: Enhanced text component with comprehensive validation
/* Custom dropdown - replaces the native <select> so the closed control
   and the option list share the app's dark styling. Native selects render
   their popup with OS chrome that cannot be themed. */
const MgSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  disabled,
  renderOption,
  optionStyle,
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // The list is portalled to <body> because .mgp-config uses overflow:hidden
  // to stay unscrollable - an in-flow list gets clipped at the card edge.
  const [box, setBox] = useState(null);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  // Measure the trigger, and decide whether to drop down or flip up.
  const place = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const LIST_MAX = 268;
    const gap = 8;
    const below = window.innerHeight - r.bottom - gap;
    const above = r.top - gap;
    const flip = below < Math.min(LIST_MAX, 180) && above > below;
    setBox({
      left: r.left,
      width: r.width,
      top: flip ? undefined : r.bottom + gap,
      bottom: flip ? window.innerHeight - r.top + gap : undefined,
      maxHeight: Math.max(120, Math.min(LIST_MAX, flip ? above : below)),
    });
  }, []);

  // Close on outside click / Escape, and keep the list pinned to the trigger.
  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDoc = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inRoot && !inList) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  const choose = (val) => {
    onChange({ target: { value: val } });
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(Math.max(0, options.indexOf(value))); return; }
      if (e.key === 'ArrowDown') setActive(i => Math.min(options.length - 1, i + 1));
      else if (active >= 0) choose(options[active]);
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      setActive(i => Math.max(0, i - 1));
    }
  };

  return (
    <div
      className={`mgp-dd${open ? ' is-open' : ''}${value ? ' has-value' : ''}${disabled ? ' is-disabled' : ''}`}
      ref={rootRef}
    >
      <button
        id={id}
        type="button"
        className="mgp-dd__trigger"
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="mgp-dd__value" style={value ? optionStyle?.(value) : undefined}>
          {value ? (renderOption ? renderOption(value) : value) : ''}
        </span>
        <span className="mgp-dd__chev" aria-hidden="true" />
      </button>

      <span className="mgp-dd__label">{label}</span>

      {open && box && createPortal(
        <ul
          ref={listRef}
          className="mgp-dd__list"
          role="listbox"
          tabIndex={-1}
          style={{
            left: box.left,
            width: box.width,
            top: box.top,
            bottom: box.bottom,
            maxHeight: box.maxHeight,
          }}
        >
          {options.map((opt, i) => (
            <li key={opt} role="option" aria-selected={opt === value}>
              <button
                type="button"
                className={`mgp-dd__opt${opt === value ? ' is-selected' : ''}${i === active ? ' is-active' : ''}`}
                style={optionStyle?.(opt)}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(opt)}
              >
                {renderOption ? renderOption(opt) : opt}
                {opt === value && <span className="mgp-dd__tick" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

const EmbroideredText = React.memo(({
  text,
  fontFamily,
  position,
  textColor,
  isMobile,
  isAdjustSelected = false
}) => {
  // SECURITY: Validate all props before rendering
  const safeText = useMemo(() => validateInput(text, 'personalised'), [text]);
  const safeFont = useMemo(() => validateInput(fontFamily, 'font'), [fontFamily]);
  const safeColor = useMemo(() => validateInput(textColor, 'color'), [textColor]);
  
  // SECURITY: Validate position object
  const safePosition = useMemo(() => {
    if (!position || typeof position !== 'object') {
      return { top: '50%', left: '50%', rotation: 0, fontSize: { desktop: 12, mobile: 6 } };
    }
    
    return {
      top: String(position.top || '50%'),
      left: String(position.left || '50%'),
      rotation: Number(position.rotation || 0),
      fontSize: position.fontSize || { desktop: 12, mobile: 6 }
    };
  }, [position]);

  const uniqueId = useMemo(() => 
    `text-${safePosition.top}-${safePosition.left}-${Math.random().toString(36).substring(2, 9)}`,
    [safePosition.top, safePosition.left]
  );

  // SECURITY: Safe stroke color calculation
  const getStrokeColor = useCallback((color) => {
    if (!color || typeof color !== 'string') return 'rgba(68, 68, 68, 0.5)';
    
    const hexToRgb = (hex) => {
      if (!hex.startsWith('#') || hex.length !== 7) return [68, 68, 68];
      
      try {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        
        if (isNaN(r) || isNaN(g) || isNaN(b)) return [68, 68, 68];
        
        return [
          Math.max(0, Math.min(255, r)),
          Math.max(0, Math.min(255, g)),
          Math.max(0, Math.min(255, b))
        ];
      } catch {
        return [68, 68, 68];
      }
    };

    const calculateLuminance = (r, g, b) => {
      const a = [r, g, b].map(v => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    };

    if (color.startsWith('#')) {
      const [r, g, b] = hexToRgb(color);
      const luminance = calculateLuminance(r, g, b);
      return luminance > 0.5 ? 'rgba(68, 68, 68, 0.5)' : 'rgba(48, 47, 47, 0.5)';
    }
    
    const lightColors = ['#ffe599', '#c0c0c0', 'beige', 'ivory', 'white'];
    return lightColors.includes(color.toLowerCase()) 
      ? 'rgba(58, 55, 55, 0.5)' 
      : 'rgba(255, 255, 255, 0.5)';
  }, []);

  const strokeColor = useMemo(() => getStrokeColor(safeColor), [safeColor, getStrokeColor]);

  // SECURITY: Fixed font size calculation with bounds checking
  const fontSize = useMemo(() => {
    const size = isMobile
      ? safePosition.fontSize.mobile
      : safePosition.fontSize.desktop;
    return Math.max(4, Math.min(50, size)); // Bounded font size
  }, [isMobile, safePosition.fontSize]);

  // SECURITY: Safe ref callback
  const refCallback = useCallback((el) => {
    if (el && safeText) {
      try {
        safeSetTextContent(el, safeText);
      } catch (error) {
        console.warn('Failed to set text content safely:', error);
      }
    }
  }, [safeText]);

  if (!safeText || !safeFont || !safeColor) {
    return null;
  }

  return (
    <div
      id={uniqueId}
      className={`embroidered-text${isAdjustSelected ? ' is-adjust-selected' : ''}`}
      style={{
        position: 'absolute',
        top: safePosition.top,
        left: safePosition.left,
        transform: `translate(-50%, -50%) ${safePosition.rotation ? `rotate(${Math.max(-360, Math.min(360, safePosition.rotation))}deg)` : ''}`,
        fontFamily: `"${safeFont}"`,
        fontSize: `${fontSize}px`,
        color: safeColor,
        fontStyle: 'italic',
        fontWeight: 'bold',
        WebkitTextStroke: `0.3px ${strokeColor}`,
        textShadow: `
          1px 1px 1px rgba(33, 33, 33, 0.28),
          -1px -1px 1px rgba(71, 71, 71, 0.56),
          0 0 2px rgba(37, 36, 36, 0.3)
        `,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 10
      }}
      ref={refCallback}
    >
      {safeText}
    </div>
  );
});

EmbroideredText.displayName = 'EmbroideredText';

const DesignArtwork = React.memo(({ design, className = '' }) => {
  if (!design) return null;

  return design.src ? (
    <span className={`${className} mgp-design-artwork`} aria-hidden="true">
      <img
        className="mgp-design-artwork__image"
        src={design.src}
        alt=""
        draggable="false"
        style={{
          transform: `scale(${design.scale || 1})`
        }}
      />
    </span>
  ) : (
    <span className={`${className} mgp-design-mark`} aria-hidden="true">
      {design.code}
    </span>
  );
});

DesignArtwork.displayName = 'DesignArtwork';

const EmbroideredDesign = React.memo(({
  design,
  position,
  isMobile,
  compact = false,
  placementScale = 1,
  isAdjustSelected = false
}) => {
  if (!design || !position) return null;

  const baseWidth = compact ? 26 : (isMobile ? 22 : 48);
  const adjustedWidth = Number(position.designSize);
  const width = Number.isFinite(adjustedWidth)
    ? adjustedWidth
    : baseWidth * placementScale;

  return (
    <div
      className={`embroidered-design${compact ? ' embroidered-design--compact' : ''}${isAdjustSelected ? ' is-adjust-selected' : ''}`}
      style={{
        top: String(position.top || '50%'),
        left: String(position.left || '50%'),
        width: `${width}px`,
        transform: `translate(-50%, -50%) ${position.rotation ? `rotate(${position.rotation}deg)` : ''}`,
      }}
      aria-hidden="true"
    >
      <DesignArtwork design={design} className="embroidered-design__art" />
    </div>
  );
});

EmbroideredDesign.displayName = 'EmbroideredDesign';

const appendDesignArtwork = (container, design, position, width = 48) => {
  if (!container || !design || !position) return Promise.resolve();

  const artwork = document.createElement('div');

  artwork.style.position = 'absolute';
  artwork.style.top = String(position.top || '50%');
  artwork.style.left = String(position.left || '50%');
  artwork.style.width = `${width}px`;
  artwork.style.height = `${width}px`;
  artwork.style.transform = `translate(-50%, -50%) ${position.rotation ? `rotate(${position.rotation}deg)` : ''}`;
  artwork.style.pointerEvents = 'none';
  artwork.style.zIndex = '10';
  let image = null;

  if (design.src) {
    artwork.style.overflow = 'visible';
    image = document.createElement('img');
    image.alt = '';
    image.crossOrigin = 'anonymous';
    image.src = design.src;
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.objectFit = 'contain';
    image.style.transform = `scale(${design.scale || 1})`;
    image.style.transformOrigin = 'center';
    artwork.appendChild(image);
  } else {
    artwork.textContent = design.code;
    artwork.style.display = 'grid';
    artwork.style.placeItems = 'center';
    artwork.style.border = '1px solid rgba(255, 255, 255, 0.78)';
    artwork.style.borderRadius = '50%';
    artwork.style.background = 'rgba(7, 8, 10, 0.78)';
    artwork.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.4)';
    artwork.style.color = '#fff';
    artwork.style.fontFamily = 'Arial, sans-serif';
    artwork.style.fontSize = `${Math.max(9, Math.round(width * 0.27))}px`;
    artwork.style.fontWeight = '700';
    artwork.style.letterSpacing = '1px';
  }

  container.appendChild(artwork);

  if (!design.src) return Promise.resolve();
  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    image.onload = finish;
    image.onerror = finish;
    if (image.complete) finish();
    setTimeout(finish, 5000);
  });
};

// SECURITY: Enhanced OrderForm component with comprehensive validation
const OrderForm = React.memo(({ 
  onClose, 
  selectedVehicleModel,
  selectedSeatView,
  selectedAccessory,
  personalisedContent,
  selectedFont,
  selectedColor,
  personalisationMode,
  selectedDesign,
  numSets,
  imageRef,
  pushToast,
  previewTextPositions,
  textColors,
  kitPrices,
  fontsLoaded,
  isMobile,
  availableRows = ['Front Row', 'Rear Row']
}) => {
  // SECURITY: Enhanced input validation functions
  const validateInput = useCallback((value, type) => {
    if (typeof value !== 'string') return '';
    
    switch (type) {
      case 'text':
        // Remove potentially dangerous characters while preserving valid text
        return value
          .replace(/[<>'"&]/g, '') // Remove HTML/script injection chars
          .replace(/javascript:/gi, '') // Remove javascript: protocol
          .replace(/data:/gi, '') // Remove data: protocol
          .replace(/\s{2,}/g, ' ') // Collapse runs, but keep single spaces:
                                   // trimming here blocks typing a space at all
          .slice(0, 100); // Limit length
          
      case 'phone':
        // Only allow digits and limit to 10 characters
        return value.replace(/\D/g, '').slice(0, 10);
        
      case 'email':
        // Basic email sanitization
        return value
          .toLowerCase()
          .replace(/[<>'"&]/g, '')
          .slice(0, 100);
          
      default:
        return value.trim().slice(0, 100);
    }
  }, []);

  const isValidPhone = useCallback((phone) => {
    return /^\d{10}$/.test(phone);
  }, []);

  const isValidEmail = useCallback((email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  const sanitizeDigits = useCallback((value) => {
    return value.replace(/\D/g, '');
  }, []);

  // SECURITY: State with validation - Initialize with proper defaults
  const [orderNo, setOrderNo] = useState('');
  const [orderDate, setOrderDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // SECURITY: Enhanced state management with validation
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [BookingID, setBookingID] = useState('');
  const [dealershipName, setDealershipName] = useState('');
  const [dealershipAddress, setDealershipAddress] = useState('');
  const [dealershipManager, setDealershipManager] = useState('');
  const [dealershipLocation, setDealershipLocation] = useState('');
  const [errors, setErrors] = useState({});
  const [showTerms, setShowTerms] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showThanks, setShowThanks] = useState(false);


    // **ADD THESE VALIDATED VARIABLES HERE:**
  const validatedVehicleModel = useMemo(() => 
    validateInput(selectedVehicleModel, 'text'), 
    [selectedVehicleModel, validateInput]
  );
  
  const validatedAccessory = useMemo(() => 
    validateInput(selectedAccessory, 'text'), 
    [selectedAccessory, validateInput]
  );
  
  const validatedPersonalisedContent = useMemo(() => 
    validateInput(personalisedContent, 'text'), 
    [personalisedContent, validateInput]
  );
  
  const validatedSelectedFont = useMemo(() => 
    validateInput(selectedFont, 'text'), 
    [selectedFont, validateInput]
  );
  
  const validatedSelectedColor = useMemo(() => 
    validateInput(selectedColor, 'text'), 
    [selectedColor, validateInput]
  );

  const validatedNumSets = useMemo(() => 
    Math.max(1, parseInt(numSets) || 1), 
    [numSets]
  );

  // SECURITY: Enhanced local toast management
  const [localToasts, setLocalToasts] = useState([]);
  const localPushToast = useCallback((message, variant = 'info', timeoutMs = 3000, onClose) => {
    // Validate inputs
    const safeMessage = validateInput(message, 'text');
    const safeVariant = ['info', 'success', 'error', 'warning'].includes(variant) ? variant : 'info';
    const safeTimeout = Math.max(1000, Math.min(10000, timeoutMs)); // Bounded timeout
    
    const id = Date.now() + Math.random();
    setLocalToasts(prev => [...prev, { id, message: safeMessage, variant: safeVariant, onClose }]);
    
    setTimeout(() => {
      setLocalToasts(prev => {
        const removed = prev.find(t => t.id === id);
        if (removed && typeof removed.onClose === 'function') {
          try { 
            removed.onClose(); 
          } catch (err) { 
            console.error('Toast onClose error:', err); 
          }
        }
        return prev.filter(t => t.id !== id);
      });
    }, safeTimeout);
  }, [validateInput]);

  // SECURITY: Safe form validation
 const validateForm = useCallback(() => {
  const newErrors = {};

  if (!customerName.trim()) {
    newErrors.customerName = 'Customer name is required';
  }

  if (!isValidPhone(customerPhone)) {
    newErrors.customerPhone = 'Valid 10-digit phone number is required';
  }

  // Optional email validation - only validate if email is provided
  if (customerEmail.trim() && !isValidEmail(customerEmail)) {
    newErrors.customerEmail = 'Please enter a valid email address';
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
}, [customerName, customerPhone, customerEmail, isValidPhone]);

  // SECURITY: Enhanced state updates with validation
  useEffect(() => {
    if (BookingID && BookingID !== orderNo) {
      const validatedBookingID = validateInput(BookingID, 'text');
      setOrderNo(validatedBookingID);
    }
  }, [BookingID, validateInput]);

  useEffect(() => {
    if (orderNo && orderNo !== BookingID) {
      const validatedOrderNo = validateInput(orderNo, 'text');
      setBookingID(validatedOrderNo);
    }
  }, [orderNo, validateInput]);

  // SECURITY: Enhanced input styling with validation feedback
  // Dark-theme field. One helper drives every input in the form, so the
  // styling stays consistent without touching each binding.
  const getInputStyle = useCallback((fieldName) => ({
    width: '100%',
    padding: '10px 12px',
    border: errors[fieldName]
      ? '1px solid #e0455a'
      : '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#f2f4f7',
    backgroundColor: 'rgba(255,255,255,0.04)',
    outline: 'none'
  }), [errors]);

  // SECURITY: Enhanced PDF generation with comprehensive validation
const handleDownloadOrder = async () => {
  // Security fix: gate by form validation with safe toast/alert
  if (typeof validateForm === 'function' && !validateForm()) {
    if (typeof localPushToast === 'function') {
      localPushToast('Please fill in all required fields correctly.', 'error');
    } else {
      alert('Please fill in all required fields correctly.');
    }
    return;
  }

  if (!orderFormRef.current) return;

  try {
    const pdfDoc = await PDFDocument.create();
    
    // Convert mm to points (1mm = 2.834645669 points)
    const mmToPt = (mm) => mm * 2.834645669;
    
    // CONFIGURABLE MARGINS - Adjust these values to increase/decrease margins
    const topMargin = mmToPt(10);
    const bottomMargin = mmToPt(10);
    const leftMargin = mmToPt(10);
    const rightMargin = mmToPt(10);

    // Load fonts
    // MG's interface typeface (mgmotor.co.in). Falls back to Helvetica if
    // the file cannot be fetched, so a font problem never blocks an order.
    pdfDoc.registerFontkit(fontkit.default || fontkit);

    let font;
    let boldFont;
    try {
      const interBytes = await (await fetch('/fonts/InterTight.ttf')).arrayBuffer();
      // Variable font: pdf-lib takes the default instance for each embed,
      // so weight is carried by size/spacing rather than a bold cut.
      // No subsetting: pdf-lib mis-subsets this variable font and drops
      // most glyphs, so the whole face is embedded instead.
      font = await pdfDoc.embedFont(interBytes);
      boldFont = font;
    } catch (fontErr) {
      console.warn('Inter Tight unavailable, falling back to Helvetica:', fontErr);
      font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    }
    
    // Get form
    const form = pdfDoc.getForm();

    // The embroidery face the customer picked, embedded so the PDF shows
    // the actual stitch style rather than describing it in body text.
    const EMBROIDERY_FONT_FILES = {
      'Book Script': '/fonts/books-script.otf',
      'Brushability': '/fonts/Brushability-W00-Script.otf',
      'Chancery': '/fonts/Apple Chancery.ttf',
      'Berlinsans': '/fonts/berlinsans.otf',
      'Georgia': '/fonts/georgia.ttf',
    };

    let embroideryFont = null;
    if (selectedFont && EMBROIDERY_FONT_FILES[selectedFont]) {
      try {
        const embBytes = await (
          await fetch(EMBROIDERY_FONT_FILES[selectedFont])
        ).arrayBuffer();
        embroideryFont = await pdfDoc.embedFont(embBytes);
      } catch (embErr) {
        console.warn('Embroidery font unavailable, using body font:', embErr);
      }
    }

    // Thread colour, parsed from the selected hex. Falls back to the
    // normal value grey when nothing is selected.
    const threadColor = (() => {
      if (!selectedColor || !/^#[0-9a-f]{6}$/i.test(selectedColor)) return null;
      return rgb(
        parseInt(selectedColor.slice(1, 3), 16) / 255,
        parseInt(selectedColor.slice(3, 5), 16) / 255,
        parseInt(selectedColor.slice(5, 7), 16) / 255
      );
    })();

    const labelColor = rgb(0, 0, 0);
    const valueColor = rgb(80/255, 80/255, 80/255);
    const sectionBg = rgb(245/255, 245/255, 245/255);

    // Written month avoids ambiguity (and the wide hyphen sidebearings in
    // Inter Tight, which made '19-08-2026' read as '19- 08- 2026').
    const cleanDate = new Date(orderDate).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

    const safeCustomerName = (customerName || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');

    // Create shared order number variable
    const sharedOrderNumber = BookingID || orderNo || '';

    // HELPER FUNCTION: Add header and footer to any page
    const addHeaderFooter = async (page, pageType = 'first') => {
      const { width: pageWidth, height: pageHeight } = page.getSize();
      
      // MG logo
      const logoMarginLeft = mmToPt(7);
      const logoMarginTop = pageHeight - mmToPt(6.5);
      const logoMarginRight = mmToPt(10);
      
      try {
        const logoResponse = await fetch('/mg-logo-dark.png');
        const logoBytes = await logoResponse.arrayBuffer();
        const logoImage = await pdfDoc.embedPng(logoBytes);

        const img = new Image();
        const logoDataUrl = URL.createObjectURL(new Blob([logoBytes]));
        img.src = logoDataUrl;

        await new Promise(resolve => {
          img.onload = resolve;
        });

        // Natural size converted to points
        let logoWidth = mmToPt(img.width * 0.264583);
        let logoHeight = mmToPt(img.height * 0.264583);
        const aspectRatio = logoWidth / logoHeight;

        // Desired max logo size
        // The MG mark is square (1:1), unlike the previous wide wordmark -
        // cap it near the header text height so it reads as a badge.
        const maxWidth = mmToPt(14);
        const maxHeight = mmToPt(14);

        // Scale within max size
        if (logoWidth > maxWidth) {
          logoWidth = maxWidth;
          logoHeight = logoWidth / aspectRatio;
        }
        if (logoHeight > maxHeight) {
          logoHeight = maxHeight;
          logoWidth = logoHeight * aspectRatio;
        }

        page.drawImage(logoImage, {
          x: logoMarginLeft,
          y: logoMarginTop - logoHeight,
          width: logoWidth,
          height: logoHeight,
        });

        URL.revokeObjectURL(logoDataUrl);
      } catch (error) {
        console.warn('Logo could not be loaded:', error);
      }

      // Add editable order number field based on page type
      if (pageType === 'first') {
        // Order info text (static)
        page.drawText(`Order No :`, {
          x: pageWidth - rightMargin - mmToPt(50),
          y: pageHeight - topMargin - mmToPt(6),
          size: 10,
          font: font,
          color: labelColor,
        });

        // Create editable Order Number field in header
        const headerOrderNoField = form.createTextField("headerOrderNo");
        headerOrderNoField.setText(sharedOrderNumber + (sharedOrderNumber && !sharedOrderNumber.endsWith('-P') ? '-P' : ''));
        headerOrderNoField.addToPage(page, {
          x: pageWidth - rightMargin - mmToPt(30),
          y: pageHeight - topMargin - mmToPt(7),
          width: mmToPt(30),
          height: mmToPt(4),
          borderWidth: 0,
          backgroundColor: rgb(1, 1, 1),
          font,
        });
        headerOrderNoField.setFontSize(9);
      } else if (pageType === 'second') {
        // Order info text (static)
        page.drawText(`Order No :`, {
          x: pageWidth - rightMargin - mmToPt(50),
          y: pageHeight - topMargin - mmToPt(6),
          size: 10,
          font: font,
          color: labelColor,
        });

        // Create editable Order Number field in second page header
        const headerOrderNoFieldPage2 = form.createTextField("headerOrderNoPage2");
        headerOrderNoFieldPage2.setText(sharedOrderNumber + (sharedOrderNumber && !sharedOrderNumber.endsWith('-P') ? '-P' : ''));
        headerOrderNoFieldPage2.addToPage(page, {
          x: pageWidth - rightMargin - mmToPt(30),
          y: pageHeight - topMargin - mmToPt(7),
          width: mmToPt(30),
          height: mmToPt(4),
          borderWidth: 0,
          backgroundColor: rgb(1, 1, 1),
          font,
        });
        headerOrderNoFieldPage2.setFontSize(9);
      }

      page.drawText(`Date : ${cleanDate}`, {
        x: pageWidth - rightMargin - mmToPt(50),
        y: pageHeight - topMargin - mmToPt(12),
        size: 10,
        font: font,
        color: valueColor,
      });

      // Header line
      page.drawLine({
        start: { x: leftMargin, y: pageHeight - topMargin - mmToPt(18) },
        end: { x: pageWidth - rightMargin, y: pageHeight - topMargin - mmToPt(18) },
        thickness: mmToPt(0.5),
        color: rgb(0.72, 0.72, 0.72),
      });

      // Footer line
      page.drawLine({
        start: { x: leftMargin, y: bottomMargin - mmToPt(2) },
        end: { x: pageWidth - rightMargin, y: bottomMargin - mmToPt(2) },
        thickness: 1,
        color: rgb(0.72, 0.72, 0.72),
      });
    };

    // ==================== FIRST PAGE ====================
    const firstPage = pdfDoc.addPage([595.28, 841.89]); // A4 size in points
    const { width: pageWidth, height: pageHeight } = firstPage.getSize();
    
    // Add header and footer to first page
    await addHeaderFooter(firstPage, 'first');
    
    let currentY = pageHeight - topMargin;

    // Helper function for invisible editable fields on FIRST PAGE
    const addInvisibleEditableField = (name, x, y, width = mmToPt(50), height = mmToPt(4), defaultValue = '') => {
      const textField = form.createTextField(name);
      textField.setText(defaultValue);
      textField.addToPage(firstPage, {
        x,
        y: y - height,
        width,
        height,
        borderWidth: 0,
        backgroundColor: rgb(1, 1, 1),
        font,
      });
      textField.setFontSize(9);
      return textField;
    };

    // Helper function for invisible editable fields on SECOND PAGE
    const addInvisibleEditableFieldPage2 = (name, x, y, width = mmToPt(50), height = mmToPt(4), defaultValue = '') => {
      const textField = form.createTextField(name);
      textField.setText(defaultValue);
      textField.addToPage(secondPage, {
        x,
        y: y - height,
        width,
        height,
        borderWidth: 0,
        backgroundColor: rgb(1, 1, 1),
        font,
      });
      textField.setFontSize(9);
      return textField;
    };

    // Updated addLabelValue function with selective editability
    const addLabelValue = (label, value, x, y, labelWidth = mmToPt(45), fieldName = '', isEditable = false, opts = {}) => {
      const safeValue = (value && value !== 'N/A') ? value : '';
      
      firstPage.drawText(label, {
        x: x,
        y: y,
        size: 9,
        font: boldFont,
        color: labelColor,
      });

      if (fieldName && isEditable) {
        addInvisibleEditableField(fieldName, x + labelWidth, y - mmToPt(1), mmToPt(60), mmToPt(4), safeValue);
      } else {
        // A row can override the face, size and colour - used for the
        // embroidery preview rows.
        firstPage.drawText(safeValue, {
          x: x + labelWidth,
          y: y,
          size: opts.valueSize || 9,
          font: opts.valueFont || font,
          color: opts.valueColor || valueColor,
        });
      }
    };

    // FIXED: Updated addLabelValueWithWrap function with proper editability
    const addLabelValueWithWrap = (label, value, x, y, labelWidth = mmToPt(45), maxWidth = mmToPt(80), fieldName = '', isEditable = false) => {
      const safeValue = (value && value !== 'N/A') ? value : '';
      
      firstPage.drawText(label, {
        x: x,
        y: y,
        size: 9,
        font: boldFont,
        color: labelColor,
      });
      
      if (fieldName && isEditable) {
        addInvisibleEditableField(fieldName, x + labelWidth, y - mmToPt(1), maxWidth - labelWidth, mmToPt(8), safeValue);
        return mmToPt(Math.max(4, 5));
      } else if (safeValue) {
        const availableWidth = maxWidth - labelWidth;
        firstPage.drawText(safeValue, {
          x: x + labelWidth,
          y: y,
          size: 9,
          font: font,
          color: valueColor,
          maxWidth: availableWidth,
        });
        return mmToPt(Math.max(4, 5));
      }
      return mmToPt(5);
    };

    currentY -= mmToPt(18);
    
    currentY -= mmToPt(4);

    // Invoice-style section head: a hairline rule with tracked small caps
    // sitting on it. Lighter than a filled band and cheaper to print.
    const addSectionHeader = (title) => {
      const tracked = title.toUpperCase().split('').join(' ');

      firstPage.drawText(tracked, {
        x: leftMargin,
        y: currentY - mmToPt(4),
        size: 8,
        font: boldFont,
        color: labelColor,
      });

      firstPage.drawLine({
        start: { x: leftMargin, y: currentY - mmToPt(6.4) },
        end: { x: pageWidth - rightMargin, y: currentY - mmToPt(6.4) },
        thickness: 0.8,
        color: rgb(0.15, 0.15, 0.15),
      });

      currentY -= mmToPt(11);
    };

    // DEALER & CUSTOMER DETAILS
    addSectionHeader('DEALER & CUSTOMER DETAILS');

    const dealerX = leftMargin;
    const customerX = pageWidth / 2 + mmToPt(5);
    let dealerY = currentY - mmToPt(3);
    let customerY = currentY - mmToPt(3);

    firstPage.drawText('DEALER INFORMATION', {
      x: dealerX,
      y: dealerY,
      size: 10,
      font: boldFont,
      color: labelColor,
    });

    firstPage.drawText('CUSTOMER INFORMATION', {
      x: customerX,
      y: customerY,
      size: 10,
      font: boldFont,
      color: labelColor,
    });

    dealerY -= mmToPt(6);
    customerY -= mmToPt(6);

    // Dealer info - Custom positioning for editable fields
    firstPage.drawText('Dealer Name :', {
      x: dealerX,
      y: dealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('dealerName', dealerX + mmToPt(45), dealerY - mmToPt(-3), mmToPt(40), mmToPt(4), dealershipName || '');
    dealerY -= mmToPt(5);

    firstPage.drawText('Accessory Manager :', {
      x: dealerX,
      y: dealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('dealerManager', dealerX + mmToPt(45), dealerY - mmToPt(-3), mmToPt(40), mmToPt(4), dealershipManager || '');
    dealerY -= mmToPt(5);

    firstPage.drawText('Dealer Location :', {
      x: dealerX,
      y: dealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('dealerLocation', dealerX + mmToPt(45), dealerY - mmToPt(-3), mmToPt(40), mmToPt(4), dealershipLocation || '');
    dealerY -= mmToPt(7);

    firstPage.drawText('Dealer Address :', {
      x: dealerX,
      y: dealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('dealerAddress', dealerX + mmToPt(45), dealerY - mmToPt(-3.5), mmToPt(40), mmToPt(5), dealershipAddress || '');
    dealerY -= mmToPt(6);

    firstPage.drawText('Booking ID / OTF No :', {
      x: dealerX,
      y: dealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    const bookingIdField = addInvisibleEditableField(
      "bookingId",
      dealerX + mmToPt(45),
      dealerY - mmToPt(-3),
      mmToPt(40),
      mmToPt(4),
      sharedOrderNumber
    );

    // Customer info - NOT editable
    addLabelValue('Customer Name :', customerName, customerX, customerY, mmToPt(35));
    customerY -= mmToPt(5);
    addLabelValue('Customer Phone :', customerPhone, customerX, customerY, mmToPt(35));
    customerY -= mmToPt(5);
    addLabelValue(customerEmail ? 'Customer Email :' : 'Customer Email :', customerEmail, customerX, customerY, mmToPt(35));
    customerY -= mmToPt(5);

    currentY = Math.min(dealerY, customerY) - mmToPt(2);
    
    firstPage.drawLine({
      start: { x: leftMargin, y: currentY },
      end: { x: pageWidth - rightMargin, y: currentY },
      thickness: 1,
      color: labelColor,
    });
    
    currentY -= mmToPt(4);

    // VEHICLE & PERSONALIZATION - NOT editable
    addSectionHeader('VEHICLE & PERSONALIZATION');
    
    addLabelValue('Vehicle Model :', selectedVehicleModel, leftMargin, currentY, mmToPt(35));
    addLabelValue('Accessory Type :', selectedAccessory, pageWidth / 2 + mmToPt(5), currentY, mmToPt(35));
    currentY -= mmToPt(6);
    
    if (personalisationMode === 'design') {
      addLabelValue('Selected Design :', selectedDesign?.name || '', leftMargin, currentY, mmToPt(35));
      currentY -= mmToPt(6);
    } else {
      addLabelValue(
        'Personalised Text :', personalisedContent,
        leftMargin, currentY, mmToPt(35),
        '', false,
        { valueFont: embroideryFont, valueColor: threadColor, valueSize: 13 }
      );
      addLabelValue(
        'Font Style :', selectedFont,
        pageWidth / 2 + mmToPt(5), currentY, mmToPt(35),
        '', false,
        { valueFont: embroideryFont, valueSize: 12 }
      );
      currentY -= mmToPt(6);

      const textColorName = textColors?.find(c => c.value === selectedColor)?.name || selectedColor;
      addLabelValue('Thread Color :', textColorName, leftMargin, currentY, mmToPt(35));

      const boxSize = mmToPt(4);
      const textWidth = font.widthOfTextAtSize(textColorName || '', 9);
      const boxX = leftMargin + mmToPt(35) + textWidth + mmToPt(3);
      const boxY = currentY - mmToPt(1);

      if (selectedColor) {
        firstPage.drawRectangle({
          x: boxX,
          y: boxY,
          width: boxSize,
          height: boxSize,
          color: rgb(
            parseInt(selectedColor.slice(1, 3), 16) / 255,
            parseInt(selectedColor.slice(3, 5), 16) / 255,
            parseInt(selectedColor.slice(5, 7), 16) / 255
          ),
          borderColor: labelColor,
          borderWidth: 0.5,
        });
      }
    }
    
    const quantityText = numSets
      ? `${numSets} ${numSets === 1 ? 'Set' : 'Sets'}`
      : '0 Sets';

    addLabelValue(
      'Quantity :',
      quantityText,
      pageWidth / 2 + mmToPt(5),
      currentY,
      mmToPt(35)
    );    
    currentY -= mmToPt(6);
    
    // Price information - NOT editable
    firstPage.drawText('MRP :', {
      x: leftMargin,
      y: currentY,
      size: 10,
      font: boldFont,
      color: labelColor,
    });

    // Withheld to match the on-screen price; see PRICE_PLACEHOLDER.
    const priceText = 'Rs. XXXX (inclusive of all taxes)';
    firstPage.drawText(priceText, {
      x: leftMargin + mmToPt(35),
      y: currentY,
      size: 9,
      font: font,
      color: valueColor,
    });

    currentY -= mmToPt(8);
    
    firstPage.drawLine({
      start: { x: leftMargin, y: currentY },
      end: { x: pageWidth - rightMargin, y: currentY },
      thickness: 1,
      color: labelColor,
    });
    
    currentY -= mmToPt(4);

    // DESIGN PREVIEW
    addSectionHeader('DESIGN PREVIEW');

    // Image capture and display - Use the same method as the working preview
    const captureSeatView = async (seatView) => {
      try {
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.style.width = '500px';
        container.style.height = '390px';
        container.style.background = '#fff';
        document.body.appendChild(container);

        const img = document.createElement('img');
        img.crossOrigin = 'anonymous';
        img.src = `/models/${selectedVehicleModel}/${seatView}/${selectedAccessory}.png`;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.position = 'absolute';
        img.style.top = '0';
        img.style.left = '0';
        container.appendChild(img);

        await new Promise((resolve, reject) => {
          let loaded = false;
          
          img.onload = () => {
            loaded = true;
            resolve();
          };
          
          img.onerror = () => {
            if (!loaded) {
              console.warn(`Image failed to load: ${seatView}`);
              const placeholder = document.createElement('div');
              placeholder.style.width = '100%';
              placeholder.style.height = '100%';
              placeholder.style.background = '#f0f0f0';
              placeholder.style.display = 'flex';
              placeholder.style.alignItems = 'center';
              placeholder.style.justifyContent = 'center';
              placeholder.innerHTML = '<p>Image not available</p>';
              container.appendChild(placeholder);
              resolve();
            }
          };
          
          setTimeout(() => {
            if (!loaded) {
              console.warn(`Image load timeout: ${seatView}`);
              resolve();
            }
          }, 5000);
        });

        const positions = personalisationMode === 'design'
          ? getDesignPositions(pdfTextPositions, selectedVehicleModel, seatView, selectedAccessory)
          : pdfTextPositions[selectedVehicleModel]?.[seatView]?.[selectedAccessory] || [];
        if (personalisationMode === 'design') {
          await Promise.all(
            positions.map(position => appendDesignArtwork(
              container,
              selectedDesign,
              position,
              34 * getDesignPlacementScale(selectedAccessory, seatView)
            ))
          );
        } else {
          positions.forEach(position => {
            const textEl = document.createElement('div');
            textEl.textContent = personalisedContent;
            textEl.style.position = 'absolute';
            textEl.style.top = position.top;
            textEl.style.left = position.left;
            textEl.style.transform = `translate(-50%, -50%) ${position.rotation ? `rotate(${position.rotation}deg)` : ''}`;
            textEl.style.fontFamily = selectedFont;
            textEl.style.fontSize = `${position.fontSize?.desktop || 8}px`;
            textEl.style.color = selectedColor;
            textEl.style.fontStyle = 'italic';
            textEl.style.fontWeight = 'bold';
            textEl.style.WebkitTextStroke = '0.3px rgba(68, 68, 68, 0.5)';
            textEl.style.textShadow = `1px 1px 1px rgba(33, 33, 33, 0.28), -1px -1px 1px rgba(71, 71, 71, 0.56), 0 0 2px rgba(37, 36, 36, 0.3)`;
            textEl.style.pointerEvents = 'none';
            textEl.style.whiteSpace = 'nowrap';
            textEl.style.zIndex = '10';
            container.appendChild(textEl);
          });
        }

        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false
        });

        document.body.removeChild(container);
        return canvas.toDataURL('image/jpeg', 0.95);
      } catch (error) {
        console.error('Error capturing seat view:', error);
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjM5MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzY2NiI+SW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
      }
    };

    try {
      const [frontImage, rearImage] = await Promise.all([
        captureSeatView('Front Row'),
        captureSeatView('Rear Row')
      ]);

      const imgW = (pageWidth - leftMargin - rightMargin - mmToPt(10)) / 2;
      
      const img1 = new Image(); 
      img1.src = frontImage;
      const img2 = new Image(); 
      img2.src = rearImage;
      
      await Promise.all([
        new Promise(resolve => img1.onload = resolve),
        new Promise(resolve => img2.onload = resolve)
      ]);
      
      const h1 = (img1.height / img1.width) * imgW;
      const h2 = (img2.height / img2.width) * imgW;
      const imgH = Math.max(h1, h2);

      const frontImageBytes = await fetch(frontImage).then(res => res.arrayBuffer());
      const rearImageBytes = await fetch(rearImage).then(res => res.arrayBuffer());
      
      const frontPdfImage = await pdfDoc.embedJpg(frontImageBytes);
      const rearPdfImage = await pdfDoc.embedJpg(rearImageBytes);

      firstPage.drawImage(frontPdfImage, {
        x: leftMargin,
        y: currentY - h1,
        width: imgW,
        height: h1,
      });

      firstPage.drawText('Front Row', {
        x: leftMargin + imgW / 2 - mmToPt(8),
        y: currentY - h1 - mmToPt(5),
        size: 10,
        font: font,
        color: labelColor,
      });

      firstPage.drawImage(rearPdfImage, {
        x: leftMargin + imgW + mmToPt(10),
        y: currentY - h2,
        width: imgW,
        height: h2,
      });

      firstPage.drawText('Rear Row', {
        x: leftMargin + imgW + mmToPt(10) + imgW / 2 - mmToPt(8),
        y: currentY - h2 - mmToPt(5),
        size: 10,
        font: font,
        color: labelColor,
      });

      currentY -= imgH + mmToPt(12);
    }
    
    catch (error) {
      console.warn('Could not load preview images:', error);
      currentY -= mmToPt(50);
    }

    // DEALERSHIP AUTHENTICATION
    addSectionHeader('DEALERSHIP AUTHENTICATION');

    firstPage.drawText('Please affix the official dealership seal and provide an authorized signature below to validate this personalisation.', {
      x: leftMargin,
      y: currentY,
      size: 8,
      font: font,
      color: valueColor,
    });

    currentY -= mmToPt(7);

    const authDealerX = leftMargin;
    const authCustomerX = pageWidth / 2 + mmToPt(5);
    let authDealerY = currentY;
    let authCustomerY = currentY;

    firstPage.drawText('Authorized Representative Name:', {
      x: authDealerX,
      y: authDealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('authRepName', authDealerX + mmToPt(55), authDealerY - mmToPt(-4), mmToPt(40), mmToPt(5), '');
    authDealerY -= mmToPt(7);

    firstPage.drawText('Signature:', {
      x: authDealerX,
      y: authDealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('signature', authDealerX + mmToPt(18), authDealerY - mmToPt(-5), mmToPt(50), mmToPt(8), '');
    authDealerY -= mmToPt(7);

    firstPage.drawText('Date:', {
      x: authDealerX,
      y: authDealerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('authDate', authDealerX + mmToPt(15), authDealerY - mmToPt(-3), mmToPt(40), mmToPt(4), '');

    authCustomerY -= mmToPt(7);

    firstPage.drawText('Customer Signature:', {
      x: authCustomerX,
      y: authCustomerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('customerSignature', authCustomerX + mmToPt(35), authCustomerY - mmToPt(-5), mmToPt(45), mmToPt(8), '');
    authCustomerY -= mmToPt(7);

    firstPage.drawText('Date:', {
      x: authCustomerX,
      y: authCustomerY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });
    addInvisibleEditableField('customerDate', authCustomerX + mmToPt(15), authCustomerY - mmToPt(-3), mmToPt(40), mmToPt(4), '');

    currentY = Math.min(authDealerY, authCustomerY) - mmToPt(7);

    firstPage.drawText('Note: Personalisation will not be processed without dealership authentication.', {
      x: leftMargin,
      y: currentY,
      size: 8,
      font: font,
      color: rgb(0.25, 0.25, 0.25),
    });

    currentY -= mmToPt(6);

    // DELIVERY TIMELINE NOTICE
    firstPage.drawText('Delivery Timeline Notice', {
      x: leftMargin,
      y: currentY,
      size: 10,
      font: boldFont,
      color: labelColor,
    });
    
    currentY -= mmToPt(5);

    const deliveryText = 'Orders may require additional processing time. Delivery timelines may vary depending on the nature of customization and Dealership Location. We appreciate your patience. Thank You for giving us the opportunity to serve you!';
    const dealershipLabel = 'Dealership Location :';
    const dealershipMessage = 'Orders need to be picked from the Dealership Location.';

    function wrapText(text, font, fontSize, maxLineWidth) {
      const words = text.split(' ');
      let line = '';
      let lines = [];

      for (let word of words) {
        const testLine = line + (line ? ' ' : '') + word;
        const testWidth = font.widthOfTextAtSize(testLine, fontSize);

        if (testWidth <= maxLineWidth) {
          line = testLine;
        } else {
          if (line) lines.push(line);
          line = word;
        }
      }
      if (line) lines.push(line);

      return lines;
    }

    const maxLineWidth = pageWidth - leftMargin - rightMargin;

    const deliveryLines = wrapText(deliveryText, font, 8, maxLineWidth);
    deliveryLines.forEach(textLine => {
      firstPage.drawText(textLine, {
        x: leftMargin,
        y: currentY,
        size: 8,
        font: font,
        color: labelColor,
      });
      currentY -= mmToPt(4);
    });

    currentY -= mmToPt(2);

    firstPage.drawText(dealershipLabel, {
      x: leftMargin,
      y: currentY,
      size: 8,
      font: boldFont,
      color: rgb(0, 0, 0),
    });

    const labelWidth = boldFont.widthOfTextAtSize(dealershipLabel + ' ', 8);

    const dealerLines = wrapText(dealershipMessage, font, 8, maxLineWidth - labelWidth);

    if (dealerLines.length > 0) {
      firstPage.drawText(dealerLines[0], {
        x: leftMargin + labelWidth,
        y: currentY,
        size: 8,
        font: font,
        color: labelColor,
      });
      currentY -= mmToPt(4);
    }

    for (let i = 1; i < dealerLines.length; i++) {
      firstPage.drawText(dealerLines[i], {
        x: leftMargin,
        y: currentY,
        size: 8,
        font: font,
        color: labelColor,
      });
      currentY -= mmToPt(4);
    }

    currentY -= mmToPt(4);

    // ==================== SECOND PAGE (Terms & Conditions) ====================
    const secondPage = pdfDoc.addPage([595.28, 841.89]); // A4 size in points
    
    // Add header and footer to second page
    await addHeaderFooter(secondPage, 'second');
    
    let page2CurrentY = pageHeight - topMargin - mmToPt(22); // Start after header

    // Terms & Conditions Section Header
    secondPage.drawRectangle({
      x: leftMargin,
      y: page2CurrentY - mmToPt(8),
      width: pageWidth - leftMargin - rightMargin,
      height: mmToPt(8),
      color: sectionBg,
    });
    
    secondPage.drawText('TERMS & CONDITIONS', {
      x: leftMargin + mmToPt(1),
      y: page2CurrentY - mmToPt(5.5),
      size: 12,
      font: boldFont,
      color: labelColor,
    });
    
    page2CurrentY -= mmToPt(16);

    const termsContent = [
      'Once the order is confirmed, it cannot be Modified and cancelled.',
      'The images represent the actual product, though the color of the image and product may slightly differ.',
      'The product will be delivered within 15 working days from the date of order confirmation.',
      'The product is non-returnable and non-refundable.'
    ];

    const maxTermsLineWidth = pageWidth - leftMargin - rightMargin - mmToPt(15);

    termsContent.forEach((term, index) => {
      secondPage.drawText('•', {
        x: leftMargin + mmToPt(5),
        y: page2CurrentY,
        size: 11,
        font: boldFont,
        color: labelColor,
      });

      const wrappedLines = wrapText(term, font, 10, maxTermsLineWidth);
      
      wrappedLines.forEach((line, lineIndex) => {
        secondPage.drawText(line, {
          x: leftMargin + mmToPt(12),
          y: page2CurrentY - (lineIndex * mmToPt(5)),
          size: 10,
          font: font,
          color: labelColor,
        });
      });

      page2CurrentY -= mmToPt(5) * wrappedLines.length + mmToPt(4);
    });

    page2CurrentY -= mmToPt(30);

    // Customer signature section with EDITABLE fields
    const signatureX = pageWidth / 2 + mmToPt(5);
    const signatureY = pageHeight/2+  mmToPt(40);
    const dateSignX = pageWidth / 2 + mmToPt(5);
    const dateSignY = signatureY - mmToPt(20);

    secondPage.drawText('Customer Signature:', {
      x: signatureX,
      y: signatureY,
      size: 9,
      font: boldFont,
      color: labelColor,
    });

    addInvisibleEditableFieldPage2('termsCustomerSignature',
      signatureX + mmToPt(35),
      signatureY + mmToPt(5),
      mmToPt(50), mmToPt(8), '');

    secondPage.drawText('Date:', {
      x: dateSignX,
      y: dateSignY+ mmToPt(11),
      size: 9,
      font: boldFont,
      color: labelColor,
    });

    addInvisibleEditableFieldPage2('termsDate',
      dateSignX + mmToPt(15),
      dateSignY + mmToPt(15),
      mmToPt(40), mmToPt(6), '');

    // Add JavaScript for field synchronization - Updated to include all order number fields
    const javascript = `
     var headerOrderNoField = this.getField("headerOrderNo");
     var headerOrderNoFieldPage2 = this.getField("headerOrderNoPage2");
     var bookingIdField = this.getField("bookingId");

     function addSuffix(value) {
       if (value == null || value.trim() === "") return "";
       if (!value.endsWith("-P")) {
         return value + "-P";
       }
       return value;
     }

     function syncAllOrderFields(sourceValue, excludeField) {
       var processedValue = addSuffix(sourceValue.toUpperCase());
       var bookingValue = sourceValue.toUpperCase().replace(/-P$/, '');
       
       if (excludeField !== "headerOrderNo" && headerOrderNoField) {
         headerOrderNoField.value = processedValue;
       }
       if (excludeField !== "headerOrderNoPage2" && headerOrderNoFieldPage2) {
         headerOrderNoFieldPage2.value = processedValue;
       }
       if (excludeField !== "bookingId" && bookingIdField) {
         bookingIdField.value = bookingValue;
       }
     }

     bookingIdField.setAction("Keystroke", "event.change = event.change.toUpperCase();");
     bookingIdField.setAction("Validate", "if (event.value != '') { syncAllOrderFields(event.value, 'bookingId'); }");
     bookingIdField.setAction("Blur", "if (event.value != '') { syncAllOrderFields(event.value, 'bookingId'); }");

     headerOrderNoField.setAction("Keystroke", "event.change = event.change.toUpperCase();");
     headerOrderNoField.setAction("Validate", "if (event.value != '') { syncAllOrderFields(event.value.replace(/-P$/, ''), 'headerOrderNo'); }");
     headerOrderNoField.setAction("Blur", "if (event.value != '') { syncAllOrderFields(event.value.replace(/-P$/, ''), 'headerOrderNo'); }");

     headerOrderNoFieldPage2.setAction("Keystroke", "event.change = event.change.toUpperCase();");
     headerOrderNoFieldPage2.setAction("Validate", "if (event.value != '') { syncAllOrderFields(event.value.replace(/-P$/, ''), 'headerOrderNoPage2'); }");
     headerOrderNoFieldPage2.setAction("Blur", "if (event.value != '') { syncAllOrderFields(event.value.replace(/-P$/, ''), 'headerOrderNoPage2'); }");
   `;

    pdfDoc.addJavaScript('syncFields', javascript);

    const pdfBytes = await pdfDoc.save();
    
    const filename = `MG_${safeCustomerName}_${cleanDate}.pdf`;
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Security fix: success toast
    if (typeof localPushToast === 'function') {
      localPushToast('Order PDF downloaded successfully!', 'success');
    }
  } catch (err) {
    console.error('PDF generation failed:', err);
    if (typeof localPushToast === 'function') {
      localPushToast('Failed to generate PDF. Please try again.', 'error');
    } else {
      alert('Something went wrong while generating the PDF.');
    }
  }
};



  // // SECURITY: Memoized validated data for rendering
  // const validatedVehicleModel = useMemo(() => 
  //   validateInput(selectedVehicleModel, 'text'), 
  //   [selectedVehicleModel, validateInput]
  // );
  
  // const validatedAccessory = useMemo(() => 
  //   validateInput(selectedAccessory, 'text'), 
  //   [selectedAccessory, validateInput]
  // );
  
  // const validatedPersonalisedContent = useMemo(() => 
  //   validateInput(personalisedContent, 'text'), 
  //   [personalisedContent, validateInput]
  // );
  
  // const validatedSelectedFont = useMemo(() => 
  //   validateInput(selectedFont, 'text'), 
  //   [selectedFont, validateInput]
  // );
  
  // const validatedSelectedColor = useMemo(() => 
  //   validateInput(selectedColor, 'text'), 
  //   [selectedColor, validateInput]
  // );

  // const validatedNumSets = useMemo(() => 
  //   Math.max(1, parseInt(numSets) || 1), 
  //   [numSets]
  // );

  // Original styles maintained
  const orderFormRef = useRef(null);

  const inputStyle = {
    width: '100%',
    padding: '4px 0',
    border: 'none',
    borderBottom: '1px solid #000',
    borderRadius: '0',
    fontSize: '14px',
    backgroundColor: 'transparent',
    outline: 'none'
  };

  const textareaStyle = {
    width: '100%',
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    minHeight: '60px',
    resize: 'vertical',
    fontFamily: 'Arial, sans-serif'
  };

  const dividerStyle = {
    width: '2px',
    backgroundColor: '#005d8f',
    margin: '0 20px',
    alignSelf: 'stretch'
  };

  return (
    <div className="mgm-overlay mgo-overlay" role="dialog" aria-modal="true">
      <div ref={orderFormRef} className="mgo">

        {/* ---------- Head ---------- */}
        <header className="mgo__head">
          <div className="mgo__brand">
            <img className="mgo__logo" src="/mg-logo-dark.png" alt="MG" />
            <span className="mgo__rule" aria-hidden="true" />
            <div>
              <span className="mgo__eyebrow">Step 3 of 3</span>
              <h2 className="mgo__title">Order Details</h2>
            </div>
          </div>

          <div className="mgo__meta">
            <div className="mgo-field mgo-field--tight">
              <label className="mgo-field__label" htmlFor="mgo-orderno">Order No.</label>
              <input
                id="mgo-orderno"
                name="orderNo"
                type="text"
                value={orderNo}
                readOnly
                className="mgo-input mgo-input--locked"
              />
            </div>
            <div className="mgo-field mgo-field--tight">
              <label className="mgo-field__label" htmlFor="mgo-date">Date</label>
              <input
                id="mgo-date"
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                className="mgo-input"
              />
            </div>
          </div>

          <button
            type="button"
            className="mgo__close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true" />
          </button>
        </header>

        {/* ---------- Body ---------- */}
        <div className="mgo__body">
          <div className="mgo__cols">

            {/* Dealer */}
            <section className="mgo-card">
              <h3 className="mgo-card__title">
                <span className="mgo-card__dot" aria-hidden="true" />
                Dealership
                <em>For office use</em>
              </h3>

              {[
                { id: 'dealershipName', label: 'Dealer Name', value: dealershipName },
                { id: 'dealershipManager', label: 'Accessory Manager', value: dealershipManager },
                { id: 'dealershipLocation', label: 'Dealer Location', value: dealershipLocation },
                { id: 'dealershipAddress', label: 'Dealer Address', value: dealershipAddress },
                { id: 'BookingID', label: 'Booking ID / OTF No.', value: BookingID },
              ].map(f => (
                <div className="mgo-field" key={f.id}>
                  <label className="mgo-field__label" htmlFor={`mgo-${f.id}`}>{f.label}</label>
                  <input
                    id={`mgo-${f.id}`}
                    name={f.id}
                    type="text"
                    value={validateInput(f.value, 'text')}
                    readOnly
                    className="mgo-input mgo-input--locked"
                  />
                </div>
              ))}
            </section>

            {/* Customer */}
            <section className="mgo-card">
              <h3 className="mgo-card__title">
                <span className="mgo-card__dot" aria-hidden="true" />
                Customer
                <em>Required</em>
              </h3>

              <div className="mgo-field">
                <label className="mgo-field__label" htmlFor="mgo-cname">
                  Full Name <b>*</b>
                </label>
                <input
                  id="mgo-cname"
                  name="customerName"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(validateInput(e.target.value, 'text'))}
                  placeholder="e.g. Rahul Sharma"
                  className={`mgo-input${errors.customerName ? ' is-error' : ''}`}
                />
                {errors.customerName && (
                  <span className="mgo-field__err">{errors.customerName}</span>
                )}
              </div>

              <div className="mgo-field">
                <label className="mgo-field__label" htmlFor="mgo-cphone">
                  Phone <b>*</b>
                </label>
                <input
                  id="mgo-cphone"
                  name="customerPhone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={customerPhone}
                  onChange={(e) => {
                    const digits = sanitizeDigits(e.target.value).slice(0, 10);
                    setCustomerPhone(digits);
                  }}
                  placeholder="10-digit mobile number"
                  className={`mgo-input${errors.customerPhone ? ' is-error' : ''}`}
                />
                {errors.customerPhone && (
                  <span className="mgo-field__err">{errors.customerPhone}</span>
                )}
              </div>

              <div className="mgo-field">
                <label className="mgo-field__label" htmlFor="mgo-cemail">
                  Email <i>optional</i>
                </label>
                <input
                  id="mgo-cemail"
                  name="customerEmail"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(validateInput(e.target.value, 'email'))}
                  placeholder="name@example.com"
                  className={`mgo-input${errors.customerEmail ? ' is-error' : ''}`}
                />
                {errors.customerEmail && (
                  <span className="mgo-field__err">{errors.customerEmail}</span>
                )}
              </div>
            </section>

            {/* Specification */}
            <section className="mgo-card mgo-card--spec">
              <h3 className="mgo-card__title">
                <span className="mgo-card__dot" aria-hidden="true" />
                Specification
              </h3>

              <dl className="mgo-spec">
                <div><dt>Vehicle</dt><dd>{validatedVehicleModel}</dd></div>
                <div><dt>Accessory</dt><dd>{validatedAccessory}</dd></div>
                {personalisationMode === 'design' ? (
                  <div>
                    <dt>Design</dt>
                    <dd className="mgo-spec__design">
                      <DesignArtwork design={selectedDesign} className="mgo-spec__design-art" />
                      {selectedDesign?.name || '—'}
                    </dd>
                  </div>
                ) : (
                  <>
                    <div>
                      <dt>Embroidery</dt>
                      <dd
                        className="mgo-spec__text"
                        style={{
                          fontFamily: validatedSelectedFont
                            ? `"${validatedSelectedFont}", serif`
                            : undefined,
                          color: validatedSelectedColor || undefined,
                        }}
                      >
                        {validatedPersonalisedContent}
                      </dd>
                    </div>
                    <div><dt>Font</dt><dd>{validatedSelectedFont || '—'}</dd></div>
                    <div>
                      <dt>Thread</dt>
                      <dd className="mgo-spec__thread">
                        <span style={{ backgroundColor: validatedSelectedColor }} aria-hidden="true" />
                        {textColors.find((c) => c.value === validatedSelectedColor)?.name || 'Unknown'}
                      </dd>
                    </div>
                  </>
                )}
                <div>
                  <dt>Quantity</dt>
                  <dd>{validatedNumSets} {validatedNumSets === 1 ? 'set' : 'sets'}</dd>
                </div>
              </dl>

              <div className="mgo-total">
                <span className="mgo-total__label">Total MRP</span>
                <span className="mgo-total__value">
                  {validatedAccessory && kitPrices && kitPrices[validatedAccessory]
                    ? PRICE_PLACEHOLDER
                    : '\u2014'}
                </span>
                <span className="mgo-total__note">Inclusive of all taxes</span>
              </div>
            </section>
          </div>

          {/* Approved design */}
          <section className="mgo-proof">
            <h3 className="mgo-card__title">
              <span className="mgo-card__dot" aria-hidden="true" />
              Approved Design
            </h3>
            <div className="mgo-proof__grid">
              {availableRows.map(row => (
                <figure className="mgo-proof__item" key={row}>
                  <div className="mgo-proof__frame">
                    <img
                      src={`/models/${validatedVehicleModel}/${row}/${validatedAccessory}.png`}
                      alt={`${row} approved design`}
                      onError={(e) => { e.target.style.visibility = 'hidden'; }}
                    />
                    {/* Live embroidery overlay - same source of truth as the
                        configurator, so the proof matches what was approved. */}
                    {personalisationMode === 'text' &&
                      fontsLoaded &&
                      validatedPersonalisedContent &&
                      previewTextPositions &&
                      getDesignPositions(
                        previewTextPositions,
                        validatedVehicleModel,
                        row,
                        validatedAccessory
                      ).map(
                        (position, index) => {
                          const fontSize = position.fontSize
                            ? (isMobile ? position.fontSize.mobile : position.fontSize.desktop)
                            : (isMobile ? 16 : 22);
                          return (
                            <div
                              key={`${row}-${index}`}
                              className="mgo-proof__stitch"
                              style={{
                                top: position.top,
                                left: position.left,
                                transform: `translate(-50%, -50%) ${position.rotation ? `rotate(${position.rotation}deg)` : ''}`,
                                fontFamily: `"${validatedSelectedFont}"`,
                                fontSize: `${fontSize}px`,
                                color: validatedSelectedColor,
                              }}
                            >
                              {validatedPersonalisedContent}
                            </div>
                          );
                        }
                      )}
                    {personalisationMode === 'design' &&
                      selectedDesign &&
                      previewTextPositions &&
                      previewTextPositions[validatedVehicleModel]?.[row]?.[validatedAccessory]?.map(
                        (position, index) => (
                          <EmbroideredDesign
                            key={`${row}-${selectedDesign.id}-${index}`}
                            design={selectedDesign}
                            position={position}
                            isMobile={isMobile}
                            compact
                            placementScale={getDesignPlacementScale(validatedAccessory, row)}
                          />
                        )
                      )}
                  </div>
                  <figcaption>{row}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        </div>

        {/* ---------- Foot ---------- */}
        <footer className="mgo__foot">
          <p className="mgo__foot-note">
            Order cannot be cancelled once confirmed.
          </p>
          <button
            type="button"
            className="mgm-btn mgm-btn--primary"
            onClick={() => {
              if (!customerName.trim()) {
                localPushToast('Please enter the customer name.', 'error');
                return;
              }
              if (!isValidPhone(customerPhone)) {
                localPushToast('Please enter a valid 10-digit phone number.', 'error');
                return;
              }
              setShowTerms(true);
            }}
          >
            <span>Place Order</span>
            <span className="mgm-btn__arrow" aria-hidden="true" />
          </button>
        </footer>

        {/* Terms & Conditions Dialog */}
        {showTerms && (
          <div className="popup-overlay">
            <div
              className="popup-box"
              style={{
                maxWidth: '560px',
                backgroundColor: '#fff',
                color: '#111',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 16px 40px rgba(0,0,0,0.2)',
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: '10px',
                  color: '#003366',
                  textAlign: 'center',
                }}
              >
                Terms & Conditions
              </h3>

              <ul
                style={{
                  marginBottom: '16px',
                  color: '#333',
                  lineHeight: 1.8,
                  listStylePosition: 'outside',
                  paddingLeft: '20px',
                  textAlign: 'justify',
                }}
              >
                <li>Once the order is confirmed, it cannot be cancelled.</li>
                <li>
                  The images represent the actual product, though the color of the image and
                  product may slightly differ.
                </li>
                <li>
                  The product will be delivered within 15 working days from the date of order
                  confirmation.
                </li>
                <li>The product is non-returnable and non-refundable.</li>
              </ul>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px',
                }}
              >
                <input
                  type="checkbox"
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                />
                <span>I agree to the terms and conditions.</span>
              </label>

              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  onClick={() => setShowTerms(false)}
                  style={{
                    border: '1px solid #E5E7EB',
                    background: '#FFFFFF',
                    color: '#374151',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  disabled={!termsChecked}
                  onClick={async () => {
                    setShowTerms(false);
                    if (!validateForm()) return;
                    await handleDownloadOrder();
                    setShowThanks(true);
                  }}
                  style={{
                    background: '#dd052b',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: termsChecked ? 'pointer' : 'not-allowed',
                    opacity: termsChecked ? 1 : 0.6,
                  }}
                >
                  Confirm Order
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Order confirmed */}
        {showThanks && (
          <div className="mgm-overlay mgt-overlay" role="alertdialog" aria-modal="true">
            <div className="mgt">
              {/* Thread stitching itself closed - the same motif as the
                  preview loader, resolved into a finished seam. */}
              <div className="mgt__seal" aria-hidden="true">
                <span className="mgt__ring" />
                <span className="mgt__tick" />
              </div>

              <span className="mgt__eyebrow">Order Confirmed</span>
              <h2 className="mgt__title">Your personalisation is on its way</h2>
              <p className="mgt__lede">
                A copy of the order has been downloaded for your records.
                Delivery within 15 working days.
              </p>

              <dl className="mgt__recap">
                <div>
                  <dt>Customer</dt>
                  <dd>{customerName || '\u2014'}</dd>
                </div>
                <div>
                  <dt>{personalisationMode === 'design' ? 'Design' : 'Embroidery'}</dt>
                  {personalisationMode === 'design' ? (
                    <dd className="mgt__design">
                      <DesignArtwork design={selectedDesign} className="mgt__design-art" />
                      {selectedDesign?.name || '\u2014'}
                    </dd>
                  ) : (
                    <dd
                      style={{
                        fontFamily: validatedSelectedFont
                          ? `"${validatedSelectedFont}", serif`
                          : undefined,
                        color: validatedSelectedColor || undefined,
                      }}
                    >
                      {validatedPersonalisedContent || '\u2014'}
                    </dd>
                  )}
                </div>
                <div>
                  <dt>Total</dt>
                  <dd>
                    {validatedAccessory && kitPrices && kitPrices[validatedAccessory]
                      ? PRICE_PLACEHOLDER
                      : '\u2014'}
                  </dd>
                </div>
              </dl>

              <button
                type="button"
                className="mgm-btn mgm-btn--primary mgt__cta"
                onClick={() => window.location.assign('/')}
                autoFocus
              >
                <span>Start a new order</span>
                <span className="mgm-btn__arrow" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* Local toasts for OrderForm */}
        {localToasts.length > 0 && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            zIndex: 4000
          }}>
            {localToasts.map(t => (
              <div key={t.id} className="toast-card" style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                minWidth: '300px',
                maxWidth: '460px',
                padding: '14px 16px',
                borderRadius: '14px',
                backgroundColor: '#ffffff',
                border: '1px solid #EEE',
                boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
                color: '#111',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '6px',
                  background: t.variant === 'error'
                    ? 'linear-gradient(180deg, #e11d2e 0%, #b3121c 100%)'
                    : 'linear-gradient(180deg, #1aa851 0%, #0f7f3a 100%)'
                }} />

                <div style={{
                  width: '10px',
                  height: '10px',
                  marginTop: '4px',
                  borderRadius: '50%',
                  backgroundColor: t.variant === 'error' ? '#e11d2e' : '#1aa851'
                }} />

                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: '#374151' }}>{t.message}</div>
                </div>

                <button
                  className="toast-close"
                  onClick={() => {
                    setLocalToasts(prev => {
                      const removed = prev.find(x => x.id === t.id);
                      if (removed && typeof removed.onClose === 'function') {
                        try { removed.onClose(); } catch (err) { console.error(err); }
                      }
                      return prev.filter(x => x.id !== t.id);
                    });
                  }}
                  aria-label="Dismiss notification"
                  style={{
                    cursor: 'pointer',
                    border: 'none',
                    background: 'transparent',
                    color: '6b7280',
                    fontSize: '16px',
                    lineHeight: 1,
                    padding: '4px',
                    position: 'absolute',
                    top: '8px',
                    right: '10px'
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

OrderForm.displayName = 'OrderForm';

// SECURITY: Enhanced PreviewPage component
const PreviewPage = React.memo(({ savedImages, availableRows, onModify, onConfirm, onClose }) => {
  // SECURITY: Validate savedImages prop
  const safeSavedImages = useMemo(() => {
    if (!savedImages || typeof savedImages !== 'object') {
      return {};
    }
    
    const validated = {};
    Object.keys(savedImages).forEach(key => {
      if (typeof key === 'string' && typeof savedImages[key] === 'string') {
        // Validate image data URL format
        if (savedImages[key].startsWith('data:image/') && savedImages[key].includes('base64,')) {
          validated[validateInput(key, 'text')] = savedImages[key];
        }
      }
    });
    
    return validated;
  }, [savedImages]);

  // SECURITY: Safe callback handlers
  const handleModify = useCallback(() => {
    if (typeof onModify === 'function') {
      try {
        onModify();
      } catch (error) {
        console.error('Error in onModify callback:', error);
      }
    }
  }, [onModify]);

  const handleConfirm = useCallback(() => {
    if (typeof onConfirm === 'function') {
      try {
        onConfirm();
      } catch (error) {
        console.error('Error in onConfirm callback:', error);
      }
    }
  }, [onConfirm]);

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      try {
        onClose();
      } catch (error) {
        console.error('Error in onClose callback:', error);
      }
    }
  }, [onClose]);

  const rows = Array.isArray(availableRows) && availableRows.length
    ? availableRows.filter(row => ['Front Row', 'Rear Row'].includes(row))
    : ['Front Row'];

  return (
    <div className="mgm-overlay" role="dialog" aria-modal="true" aria-label="Preview">
      <div className="mgm mgm--wide">
        <header className="mgm__head">
          <div>
            <span className="mgm__eyebrow">Step 2 of 3</span>
            <h2 className="mgm__title">Review your personalisation</h2>
            <p className="mgm__lede">
              {rows.length > 1
                ? 'Check both rows before confirming. Embroidery is stitched exactly as shown.'
                : 'Check the front row before confirming. Embroidery is stitched exactly as shown.'}
            </p>
          </div>
          <button
            type="button"
            className="mgm__close"
            onClick={handleClose}
            aria-label="Close preview"
          >
            <span aria-hidden="true" />
          </button>
        </header>

        <div className="mgm__body">
          <div className="mgm-rows">
            {rows.map(row => (
              <figure className="mgm-row" key={row}>
                <div className="mgm-row__frame">
                  {safeSavedImages[row] ? (
                    <img
                      src={safeSavedImages[row]}
                      alt={`${row} preview`}
                      onError={(e) => {
                        console.warn(`Failed to load ${row} image`);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="mgm-row__empty">Not saved yet</div>
                  )}
                </div>
                <figcaption className="mgm-row__cap">
                  <span className="mgm-row__dot" aria-hidden="true" />
                  {row}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <footer className="mgm__foot">
          <button type="button" className="mgm-btn mgm-btn--ghost" onClick={handleModify}>
            Modify
          </button>
          <button type="button" className="mgm-btn mgm-btn--primary" onClick={handleConfirm}>
            <span>Confirm Personalisation</span>
            <span className="mgm-btn__arrow" aria-hidden="true" />
          </button>
        </footer>
      </div>
    </div>
  );
});

PreviewPage.displayName = 'PreviewPage';

// SECURITY: Enhanced main App component
const App = () => {
  // SECURITY: State with validation
  const [selectedVehicleModel, setSelectedVehicleModel] = useState('');
  const [selectedSeatView, setSelectedSeatView] = useState('Front Row');
  const [selectedAccessory, setSelectedAccessory] = useState('');
  const [personalisedContent, setPersonalisedContent] = useState('');
  const [personalisationMode, setPersonalisationMode] = useState('text');
  const [selectedDesignId, setSelectedDesignId] = useState('');
  const [selectedFont, setSelectedFont] = useState('');
  const [selectedColor, setSelectedColor] = useState(textColors[0].value);
  const [numSets, setNumSets] = useState(1);
  const imageRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [downloadedFileName, setDownloadedFileName] = useState('');
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isAdjustMode, setIsAdjustMode] = useState(false);
  const [adjustablePositions, setAdjustablePositions] = useState([]);
  const [activePlacementIndex, setActivePlacementIndex] = useState(0);
  const [placementStep, setPlacementStep] = useState(0.5);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const orderFormRef = useRef(null);
  const [savedImages, setSavedImages] = useState({});
  const [lastEditedRow, setLastEditedRow] = useState('Front Row');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [primarySeatView, setPrimarySeatView] = useState(null);
  const [actionPopup, setActionPopup] = useState({ open: false, message: '' });
  const [showCustomDesignNotice, setShowCustomDesignNotice] = useState(false);
  const [forceFlowAfterModify, setForceFlowAfterModify] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const availableSeatViews = useMemo(
    () => accessorySeatViews[selectedAccessory] || ['Front Row'],
    [selectedAccessory]
  );
  const [customDesigns, setCustomDesigns] = useState(() => {
    try {
      const stored = window.localStorage.getItem(CUSTOM_DESIGN_STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(entry => isCustomDesignId(entry?.id) && typeof entry?.src === 'string' && entry.src.startsWith('data:image/'))
        .slice(0, CUSTOM_DESIGN_MAX_SLOTS)
        .map(entry => ({
          id: entry.id,
          name: typeof entry.name === 'string' && entry.name ? entry.name.slice(0, 18) : 'Custom',
          code: 'C',
          src: entry.src,
        }));
    } catch {
      return [];
    }
  });
  const [customDesignError, setCustomDesignError] = useState('');
  const [isProcessingCustomDesign, setIsProcessingCustomDesign] = useState(false);
  const [customDesignProgress, setCustomDesignProgress] = useState({ step: 'warm', ratio: null, index: 0, total: 0 });
  const customDesignInputRef = useRef(null);

  const availableDesigns = useMemo(
    () => [...designOptions, ...customDesigns],
    [customDesigns]
  );

  const selectedDesign = useMemo(
    () => availableDesigns.find(design => design.id === selectedDesignId) || null,
    [availableDesigns, selectedDesignId]
  );

  useEffect(() => {
    try {
      if (customDesigns.length) {
        window.localStorage.setItem(CUSTOM_DESIGN_STORAGE_KEY, JSON.stringify(
          customDesigns.map(({ id, name, src }) => ({ id, name, src }))
        ));
      } else {
        window.localStorage.removeItem(CUSTOM_DESIGN_STORAGE_KEY);
      }
    } catch {
      // Quota or private-mode failures should not break personalisation.
    }
  }, [customDesigns]);

  const handleCustomDesignUpload = useCallback(async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    setCustomDesignError('');

    let slotsLeft = CUSTOM_DESIGN_MAX_SLOTS - customDesigns.length;
    if (slotsLeft <= 0) {
      setCustomDesignError(`You can save up to ${CUSTOM_DESIGN_MAX_SLOTS} custom designs. Remove one to add another.`);
      input.value = '';
      return;
    }

    setIsProcessingCustomDesign(true);
    setCustomDesignProgress({ step: 'warm', ratio: null, index: 0, total: files.length });
    const added = [];
    const failures = [];
    let usedFallbackForAny = false;
    let processedCount = 0;

    for (const file of files) {
      if (slotsLeft <= 0) {
        failures.push(`${file.name}: no slots left`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        failures.push(`${file.name}: not an image`);
        continue;
      }
      if (file.size > CUSTOM_DESIGN_MAX_BYTES) {
        failures.push(`${file.name}: over 8MB`);
        continue;
      }

      try {
        setCustomDesignProgress({ step: 'warm', ratio: null, index: processedCount, total: files.length });
        const { src: cleanedSrc, usedFallback } = await prepareCustomDesignSource(
          file,
          (step, ratio) => setCustomDesignProgress({
            step,
            ratio,
            index: processedCount,
            total: files.length,
          }),
        );
        const label = file.name.replace(/\.[^.]+$/, '').slice(0, 18) || 'Custom';

        if (usedFallback) usedFallbackForAny = true;

        added.push({
          id: `${CUSTOM_DESIGN_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: label,
          code: 'C',
          src: cleanedSrc,
        });
        slotsLeft -= 1;
        processedCount += 1;
      } catch (error) {
        failures.push(`${file.name}: ${error?.message || 'could not be processed'}`);
      }
    }

    if (added.length) {
      setCustomDesigns(current => [...current, ...added].slice(0, CUSTOM_DESIGN_MAX_SLOTS));
      setSelectedDesignId(added[added.length - 1].id);
      pushToast(added.length > 1 ? `${added.length} custom designs added` : 'Custom design added', 'success');
    }
    if (failures.length) {
      setCustomDesignError(failures.join(' • '));
    } else if (usedFallbackForAny) {
      setCustomDesignError('Advanced cut-out was unavailable, so a basic one was used. Re-upload to try again.');
    }

    setIsProcessingCustomDesign(false);
    input.value = '';
    // pushToast is defined below and is stable; listing it here would hit the TDZ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customDesigns.length]);

  const removeCustomDesign = useCallback((designId) => {
    setCustomDesigns(current => current.filter(design => design.id !== designId));
    setCustomDesignError('');
    setSelectedDesignId(current => (current === designId ? '' : current));
  }, []);

  const createAdjustablePositions = useCallback(() => {
    if (!selectedVehicleModel || !selectedAccessory || !selectedSeatView) return [];

    const sourcePositions = personalisationMode === 'design'
      ? getDesignPositions(
          textPositions,
          selectedVehicleModel,
          selectedSeatView,
          selectedAccessory
        )
      : textPositions[selectedVehicleModel]?.[selectedSeatView]?.[selectedAccessory] || [];

    const placementScale = getDesignPlacementScale(selectedAccessory, selectedSeatView);

    return sourcePositions.map(position => ({
      ...position,
      fontSize: position.fontSize ? { ...position.fontSize } : undefined,
      ...(personalisationMode === 'design'
        ? { designSize: Number(position.designSize) || 48 * placementScale }
        : {})
    }));
  }, [
    selectedVehicleModel,
    selectedAccessory,
    selectedSeatView,
    personalisationMode
  ]);

  // SECURITY: Enhanced effect hooks with cleanup and validation
  useEffect(() => {
    let mounted = true;

    loadFonts().then(() => {
      if (mounted) {
        setFontsLoaded(true);
      }
    }).catch(error => {
      console.warn('Font loading failed:', error);
      if (mounted) {
        setFontsLoaded(true);
      }
    });

    const style = document.createElement('style');
    style.textContent = extraStyles;
    document.head.appendChild(style);

    const handleResize = () => {
      if (mounted) {
        setIsMobile(window.innerWidth < 768);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      mounted = false;
      window.removeEventListener('resize', handleResize);
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  // SECURITY: Enhanced position updates with validation
  useEffect(() => {
    setAdjustablePositions(createAdjustablePositions());
    setActivePlacementIndex(0);
  }, [createAdjustablePositions]);

  // SECURITY: Enhanced image path generation with validation
  const getImagePath = useCallback(() => {
    if (!selectedVehicleModel || !selectedAccessory || !selectedSeatView) {
      return '/Inside Page Banner.png';
    }

    const safeModel = validateInput(selectedVehicleModel, 'vehicleModel');
    const safeAccessory = validateInput(selectedAccessory, 'accessory');
    const safeSeatView = ['Front Row', 'Rear Row'].includes(selectedSeatView) ? selectedSeatView : 'Front Row';

    if (!safeModel || !safeAccessory) {
      return '/Inside Page Banner.png';
    }

    return `/models/${safeModel}/${safeSeatView}/${safeAccessory}.png`;
  }, [selectedVehicleModel, selectedAccessory, selectedSeatView]);

  // SECURITY: Enhanced download handler with comprehensive validation
  const handleDownload = useCallback(async () => {
    const validatedModel = validateInput(selectedVehicleModel, 'vehicleModel');
    const validatedAccessory = validateInput(selectedAccessory, 'accessory');
    const validatedSeatView = ['Front Row', 'Rear Row'].includes(selectedSeatView) ? selectedSeatView : '';

    if (!validatedModel || !validatedAccessory || !validatedSeatView) {
      setShowPopup(true);
      return;
    }

    const element = imageRef.current;
    if (!element || !document.contains(element)) {
      console.error('Invalid element for download');
      return;
    }

    try {
      setIsImageLoading(true);

      const canvas = await html2canvas(element, {
        scale: Math.min(4, window.devicePixelRatio * 2), // Limit scale for performance
        useCORS: true,
        allowTaint: false, // Enhanced security
        backgroundColor: null,
        timeout: 30000 // 30 second timeout
      });

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Invalid canvas generated');
      }

      const imgWidth = Math.max(1, canvas.width);
      const imgHeight = Math.max(1, canvas.height);

      const orientation = imgWidth > imgHeight ? 'landscape' : 'portrait';
      const pdf = new jsPDF(orientation, 'mm', 'a4');

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const availableWidth = Math.max(1, pageWidth - (2 * margin));
      const availableHeight = Math.max(1, pageHeight - (2 * margin));

      const ratio = Math.min(
        availableWidth / imgWidth,
        availableHeight / imgHeight
      );

      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;

      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        Math.max(0, x),
        Math.max(0, y),
        Math.max(1, scaledWidth),
        Math.max(1, scaledHeight)
      );

      // SECURITY: Safe filename generation
      const vehicle = validatedModel || 'vehicle';
      const accessory = validatedAccessory || 'accessory';
      const seat = validatedSeatView === 'Front Row' ? 'front' : 'rear';

      const cleanFilename = `${vehicle}-${seat}-${accessory}`
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9\-]/g, '')
        .toLowerCase()
        .substring(0, 50); // Limit filename length

      const filename = `${cleanFilename}.pdf`;
      setDownloadedFileName(filename);
      pdf.save(filename);

      setIsImageLoading(false);
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsImageLoading(false);

      try {
        // Fallback to image download
        const canvas = await html2canvas(element, { scale: 2 });
        const link = document.createElement('a');
        
        const vehicle = validatedModel || 'vehicle';
        const accessory = validatedAccessory || 'accessory';
        const seat = validatedSeatView === 'Front Row' ? 'front' : 'rear';

        const cleanFilename = `${vehicle}-${seat}-${accessory}`
          .trim()
          .replace(/\s+/g, '-')
          .replace(/[^a-zA-Z0-9\-]/g, '')
          .toLowerCase()
          .substring(0, 50);

        const filename = `${cleanFilename}.jpg`;
        link.download = filename;
        link.href = canvas.toDataURL('image/jpeg', 0.95);
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDownloadedFileName(filename);
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 3000);
      } catch (fallbackError) {
        console.error("Fallback error:", fallbackError);
        pushToast("Failed to generate download. Please try again.", 'error');
      }
    }
  }, [selectedVehicleModel, selectedAccessory, selectedSeatView, imageRef]);

  // SECURITY: Enhanced save handler with validation
  const handleSave = useCallback(async () => {
    const validatedModel = validateInput(selectedVehicleModel, 'vehicleModel');
    const validatedAccessory = validateInput(selectedAccessory, 'accessory');
    const validatedSeatView = ['Front Row', 'Rear Row'].includes(selectedSeatView) ? selectedSeatView : '';
    const validatedContent = validateInput(personalisedContent, 'personalised');
    const hasPersonalisation = personalisationMode === 'design'
      ? Boolean(selectedDesign)
      : Boolean(validatedContent.trim());

    if (!validatedModel || !validatedAccessory || !validatedSeatView || !hasPersonalisation) {
      setShowPopup(true);
      return;
    }

    const element = imageRef.current;
    if (!element || !document.contains(element)) {
      console.error('Invalid element for save');
      return;
    }

    try {
      const canvas = await html2canvas(element, {
        scale: Math.min(2, window.devicePixelRatio), // Limit scale for performance
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        timeout: 15000 // 15 second timeout
      });

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Invalid canvas generated for save');
      }

      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      
      // SECURITY: Validate data URL format
      if (!dataUrl.startsWith('data:image/jpeg;base64,')) {
        throw new Error('Invalid image data format');
      }

      setSavedImages(prev => ({ ...prev, [validatedSeatView]: dataUrl }));
      setLastEditedRow(validatedSeatView);
      
      if (!primarySeatView) {
        setPrimarySeatView(validatedSeatView);
      }
      
      pushToast(`${validatedSeatView} saved successfully`, 'success');
    } catch (error) {
      console.error('Error saving design:', error);
      pushToast('Failed to save the current design. Please try again.', 'error');
    }
  }, [
    selectedVehicleModel,
    selectedAccessory,
    selectedSeatView,
    personalisedContent,
    personalisationMode,
    selectedDesign,
    imageRef,
    primarySeatView
  ]);

  // SECURITY: Enhanced toast management
  const pushToast = useCallback((message, variant = 'success', timeoutMs = 2500) => {
    const safeMessage = validateInput(message, 'text');
    const safeVariant = ['success', 'error', 'info', 'warning'].includes(variant) ? variant : 'success';
    const safeTimeout = Math.max(1000, Math.min(10000, timeoutMs));
    
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message: safeMessage, variant: safeVariant }]);
    
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, safeTimeout);
  }, []);

  // SECURITY: Enhanced preview click handler with comprehensive validation
  const handlePreviewClick = useCallback(async () => {
    const validatedModel = validateInput(selectedVehicleModel, 'vehicleModel');
    const validatedAccessory = validateInput(selectedAccessory, 'accessory');
    const validatedContent = validateInput(personalisedContent, 'personalised');
    const validatedColor = validateInput(selectedColor, 'color');
    const validatedFont = validateInput(selectedFont, 'font');
    const hasTextPersonalisation = personalisationMode === 'text' &&
      Boolean(validatedContent.trim() && validatedColor && validatedFont);
    const hasDesignPersonalisation = personalisationMode === 'design' && Boolean(selectedDesign);

    if (!validatedModel || !validatedAccessory ||
        (!hasTextPersonalisation && !hasDesignPersonalisation)) {
      setActionPopup({
        open: true,
        message: personalisationMode === 'design'
          ? 'Please select a design to view the Preview.'
          : 'Please add your text to view the Preview.'
      });
      return;
    }

    setIsPreviewLoading(true);

    const generatePreviewImages = async () => {
      const saveImageForRow = async (row) => {
        if (!['Front Row', 'Rear Row'].includes(row)) {
          throw new Error('Invalid row specified');
        }

        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.style.width = '500px';
        container.style.height = '390px';
        container.style.background = '#fff';
        container.style.zIndex = '-1';
        document.body.appendChild(container);

        try {
          const img = document.createElement('img');
          img.crossOrigin = 'anonymous';
          img.src = `/models/${validatedModel}/${row}/${validatedAccessory}.png`;
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.position = 'absolute';
          img.style.top = '0';
          img.style.left = '0';
          container.appendChild(img);

          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Image load timeout')), 10000);
            
            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            
            img.onerror = () => {
              clearTimeout(timeout);
              reject(new Error('Image load failed'));
            };
          });

          const positions = personalisationMode === 'design'
            ? getDesignPositions(previewTextPositions, validatedModel, row, validatedAccessory)
            : previewTextPositions[validatedModel]?.[row]?.[validatedAccessory] || [];
          if (personalisationMode === 'design') {
            await Promise.all(
              positions
                .filter(position => position && typeof position === 'object')
                .map(position => appendDesignArtwork(
                  container,
                  selectedDesign,
                  position,
                  48 * getDesignPlacementScale(validatedAccessory, row)
                ))
            );
          } else {
            positions.forEach(position => {
              if (!position || typeof position !== 'object') return;

              const textEl = document.createElement('div');
              textEl.textContent = validatedContent;
              textEl.style.position = 'absolute';
              textEl.style.top = String(position.top || '50%');
              textEl.style.left = String(position.left || '50%');
              textEl.style.transform = `translate(-50%, -50%) ${position.rotation ? `rotate(${Math.max(-360, Math.min(360, Number(position.rotation) || 0))}deg)` : ''}`;
              textEl.style.fontFamily = validatedFont || 'Arial';
              textEl.style.fontSize = `${Math.max(8, Math.min(50, position.fontSize?.desktop || 14))}px`;
              textEl.style.color = validatedColor;
              textEl.style.fontStyle = 'italic';
              textEl.style.fontWeight = 'bold';
              textEl.style.WebkitTextStroke = '0.3px rgba(68, 68, 68, 0.5)';
              textEl.style.textShadow = '1px 1px 1px rgba(33, 33, 33, 0.28), -1px -1px 1px rgba(71, 71, 71, 0.56), 0 0 2px rgba(37, 36, 36, 0.3)';
              textEl.style.pointerEvents = 'none';
              textEl.style.whiteSpace = 'nowrap';
              textEl.style.zIndex = '10';
              container.appendChild(textEl);
            });
          }

          const canvas = await html2canvas(container, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: null,
            timeout: 15000
          });

          if (!canvas || canvas.width === 0 || canvas.height === 0) {
            throw new Error('Invalid canvas generated');
          }

          return canvas.toDataURL('image/jpeg', 0.95);
        } finally {
          if (container.parentNode) {
            document.body.removeChild(container);
          }
        }
      };

      try {
        const rows = accessorySeatViews[validatedAccessory] || ['Front Row'];
        const images = await Promise.all(rows.map(saveImageForRow));

        // SECURITY: Validate generated images
        if (images.some(image => !image.startsWith('data:image/jpeg;base64,'))) {
          throw new Error('Invalid image data generated');
        }

        return Object.fromEntries(rows.map((row, index) => [row, images[index]]));
      } catch (error) {
        console.error('Error generating preview images:', error);
        throw error;
      }
    };

    try {
      const [, generatedImages] = await Promise.all([
        new Promise(resolve => setTimeout(resolve, 2000)),
        generatePreviewImages()
      ]);

      setSavedImages(generatedImages);
      setIsPreviewLoading(false);
      setIsPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setIsPreviewLoading(false);
      pushToast('Failed to generate preview. Please try again.', 'error');
    }
  }, [
    selectedVehicleModel, 
    selectedAccessory, 
    personalisedContent, 
    selectedColor, 
    selectedFont,
    personalisationMode,
    selectedDesign,
    pushToast
  ]);

  // SECURITY: Enhanced input change handlers with validation
  const handleVehicleModelChange = useCallback((e) => {
    const validated = validateInput(e.target.value, 'vehicleModel');
    setSelectedVehicleModel(validated);
  }, []);

  const handleAccessoryChange = useCallback((e) => {
    const validated = validateInput(e.target.value, 'accessory');
    setSelectedAccessory(validated);
    const supportedRows = accessorySeatViews[validated] || ['Front Row'];
    setSelectedSeatView(currentRow => (
      supportedRows.includes(currentRow) ? currentRow : supportedRows[0]
    ));
  }, []);

  const handlePersonalisedContentChange = useCallback((e) => {
    const validated = validateInput(e.target.value.slice(0, 7), 'personalised');
    setPersonalisedContent(validated);
  }, []);

  const handlePersonalisationModeChange = useCallback((mode) => {
    if (!['text', 'design'].includes(mode)) return;
    setPersonalisationMode(mode);
  }, []);

  const handleDesignChange = useCallback((designId) => {
    if (!availableDesigns.some(design => design.id === designId)) return;
    setSelectedDesignId(designId);
  }, [availableDesigns]);

  const handleFontChange = useCallback((e) => {
    const validated = validateInput(e.target.value, 'font');
    setSelectedFont(validated);
  }, []);

  const handleColorChange = useCallback((color) => {
    const validated = validateInput(color, 'color');
    setSelectedColor(validated);
  }, []);

  const handleNumSetsChange = useCallback((e) => {
    const validated = validateInput(e.target.value, 'number');
    setNumSets(validated);
  }, []);

  const handleSeatViewChange = useCallback((value) => {
    if (!['Front Row', 'Rear Row'].includes(value)) return;
    if (!availableSeatViews.includes(value)) return;
    setSelectedSeatView(value);
  }, [availableSeatViews]);

  // SECURITY: Enhanced preload font function
  const preloadSelectedFont = useCallback(() => {
    if (personalisationMode !== 'text' || !selectedFont) return null;
    
    const validatedFont = validateInput(selectedFont, 'font');
    const validatedContent = validateInput(personalisedContent, 'personalised');
    
    return (
      <div 
        style={{ 
          fontFamily: validatedFont, 
          fontStyle: 'italic',
          position: 'absolute', 
          visibility: 'hidden', 
          fontSize: '22px',
          zIndex: -1
        }}
      >
        {validatedContent || "Preload Text"}
      </div>
    );
  }, [personalisationMode, selectedFont, personalisedContent]);

  // SECURITY: Enhanced position update handler
  const updatePosition = useCallback((index, property, value) => {
    if (typeof index !== 'number' || index < 0) return;
    if (typeof property !== 'string') return;
    
    setAdjustablePositions(prev => {
      if (!Array.isArray(prev) || index >= prev.length) return prev;
      
      const newPositions = [...prev];
      const safeValue = property === 'rotation'
        ? Math.max(-360, Math.min(360, Number(value) || 0))
        : value;
        
      newPositions[index] = {
        ...newPositions[index],
        [property]: safeValue
      };
      return newPositions;
    });
  }, []);

  const activePlacement = adjustablePositions[activePlacementIndex] || null;
  const activePlacementSize = useMemo(() => {
    if (!activePlacement) return 0;
    return personalisationMode === 'design'
      ? Number(activePlacement.designSize) || 0
      : Number(activePlacement.fontSize?.desktop) || 0;
  }, [activePlacement, personalisationMode]);

  const adjustPanelRef = useRef(null);
  const [adjustPanelOffset, setAdjustPanelOffset] = useState(null);
  const adjustDragRef = useRef(null);
  const [isAdjustDragging, setIsAdjustDragging] = useState(false);

  const clampAdjustOffset = useCallback((x, y) => {
    const panel = adjustPanelRef.current;
    const stage = panel?.offsetParent;
    if (!panel || !stage) return { x, y };
    const maxX = Math.max(0, stage.clientWidth - panel.offsetWidth);
    const maxY = Math.max(0, stage.clientHeight - panel.offsetHeight);
    return {
      x: Math.max(0, Math.min(maxX, x)),
      y: Math.max(0, Math.min(maxY, y)),
    };
  }, []);

  const startAdjustDrag = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target.closest('button, input, select, textarea')) return;

    const panel = adjustPanelRef.current;
    const stage = panel?.offsetParent;
    if (!panel || !stage) return;

    const panelBox = panel.getBoundingClientRect();
    const stageBox = stage.getBoundingClientRect();

    adjustDragRef.current = {
      pointerId: event.pointerId,
      grabX: event.clientX - panelBox.left,
      grabY: event.clientY - panelBox.top,
      stageLeft: stageBox.left,
      stageTop: stageBox.top,
    };

    // Freeze the current visual position before switching to offset-driven placement.
    setAdjustPanelOffset(clampAdjustOffset(
      panelBox.left - stageBox.left,
      panelBox.top - stageBox.top,
    ));
    setIsAdjustDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }, [clampAdjustOffset]);

  const moveAdjustDrag = useCallback((event) => {
    const drag = adjustDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setAdjustPanelOffset(clampAdjustOffset(
      event.clientX - drag.stageLeft - drag.grabX,
      event.clientY - drag.stageTop - drag.grabY,
    ));
  }, [clampAdjustOffset]);

  const endAdjustDrag = useCallback((event) => {
    const drag = adjustDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    adjustDragRef.current = null;
    setIsAdjustDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  // Keep the panel inside the stage when it resizes.
  useEffect(() => {
    if (!adjustPanelOffset) return undefined;
    const handleResize = () => {
      setAdjustPanelOffset(current => (current ? clampAdjustOffset(current.x, current.y) : current));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [adjustPanelOffset, clampAdjustOffset]);

  const setPlacementCoordinate = useCallback((property, rawValue) => {
    if (rawValue === '') return;
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) return;
    const boundedValue = Math.max(0, Math.min(100, numericValue));
    updatePosition(activePlacementIndex, property, `${Number(boundedValue.toFixed(2))}%`);
  }, [activePlacementIndex, updatePosition]);

  const nudgePlacement = useCallback((horizontalDelta, verticalDelta) => {
    setAdjustablePositions(previous => previous.map((position, index) => {
      if (index !== activePlacementIndex) return position;

      const currentLeft = Number.parseFloat(position.left) || 0;
      const currentTop = Number.parseFloat(position.top) || 0;
      const nextLeft = Math.max(0, Math.min(100, currentLeft + horizontalDelta * placementStep));
      const nextTop = Math.max(0, Math.min(100, currentTop + verticalDelta * placementStep));

      return {
        ...position,
        left: `${Number(nextLeft.toFixed(2))}%`,
        top: `${Number(nextTop.toFixed(2))}%`
      };
    }));
  }, [activePlacementIndex, placementStep]);

  const resizePlacement = useCallback((delta) => {
    setAdjustablePositions(previous => previous.map((position, index) => {
      if (index !== activePlacementIndex) return position;

      if (personalisationMode === 'design') {
        const currentSize = Number(position.designSize) ||
          48 * getDesignPlacementScale(selectedAccessory, selectedSeatView);
        return {
          ...position,
          designSize: Math.max(8, Math.min(120, currentSize + delta))
        };
      }

      const currentDesktopSize = Number(position.fontSize?.desktop) || 12;
      const nextDesktopSize = Math.max(4, Math.min(72, currentDesktopSize + delta));
      return {
        ...position,
        fontSize: {
          ...position.fontSize,
          desktop: nextDesktopSize,
          mobile: Math.max(4, Number((nextDesktopSize * 0.45).toFixed(1)))
        }
      };
    }));
  }, [activePlacementIndex, personalisationMode, selectedAccessory, selectedSeatView]);

  const setPlacementSize = useCallback((rawValue) => {
    if (rawValue === '') return;
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) return;

    setAdjustablePositions(previous => previous.map((position, index) => {
      if (index !== activePlacementIndex) return position;

      if (personalisationMode === 'design') {
        return {
          ...position,
          designSize: Math.max(8, Math.min(120, numericValue))
        };
      }

      const desktopSize = Math.max(4, Math.min(72, numericValue));
      return {
        ...position,
        fontSize: {
          ...position.fontSize,
          desktop: desktopSize,
          mobile: Math.max(4, Number((desktopSize * 0.45).toFixed(1)))
        }
      };
    }));
  }, [activePlacementIndex, personalisationMode]);

  const resetPlacementTool = useCallback(() => {
    setAdjustablePositions(createAdjustablePositions());
    setActivePlacementIndex(0);
  }, [createAdjustablePositions]);

  const copyPlacementCoordinates = useCallback(async () => {
    const payload = {
      vehicle: selectedVehicleModel,
      kit: selectedAccessory,
      row: selectedSeatView,
      mode: personalisationMode,
      positions: adjustablePositions.map((position, index) => {
        const top = Number.parseFloat(position.top) || 0;
        const left = Number.parseFloat(position.left) || 0;
        return {
          target: index === 0 ? 'left' : (index === 1 ? 'right' : `right-${index}`),
          top,
          left,
          right: Number((100 - left).toFixed(2)),
          bottom: Number((100 - top).toFixed(2)),
          size: personalisationMode === 'design'
            ? Number(position.designSize) || 0
            : Number(position.fontSize?.desktop) || 0
        };
      })
    };
    const coordinateText = JSON.stringify(payload, null, 2);

    try {
      await navigator.clipboard.writeText(coordinateText);
      pushToast('Placement coordinates copied', 'success');
    } catch {
      const copyArea = document.createElement('textarea');
      copyArea.value = coordinateText;
      copyArea.style.position = 'fixed';
      copyArea.style.opacity = '0';
      document.body.appendChild(copyArea);
      copyArea.select();
      document.execCommand('copy');
      document.body.removeChild(copyArea);
      pushToast('Placement coordinates copied', 'success');
    }
  }, [
    selectedVehicleModel,
    selectedAccessory,
    selectedSeatView,
    personalisationMode,
    adjustablePositions,
    pushToast
  ]);

  // SECURITY: Enhanced component flow control
  const isBothSaved = useMemo(() => 
    Boolean(savedImages['Front Row'] && savedImages['Rear Row']), 
    [savedImages]
  );

  const getOppositeRow = useCallback((row) => 
    row === 'Front Row' ? 'Rear Row' : 'Front Row', 
    []
  );

  // Render logic with security enhancements
  if (showOrderForm) {
    return (
      <OrderForm 
        onClose={() => {
          setShowOrderForm(false);
          setIsPreviewOpen(true);
        }}
selectedVehicleModel={validateInput(selectedVehicleModel, 'vehicleModel')}
  selectedSeatView={selectedSeatView}
  selectedAccessory={validateInput(selectedAccessory, 'accessory')}
  personalisedContent={validateInput(personalisedContent, 'personalised')}
  selectedFont={validateInput(selectedFont, 'font')}
  selectedColor={validateInput(selectedColor, 'color')}
  personalisationMode={personalisationMode}
  selectedDesign={selectedDesign}
  numSets={validateInput(numSets, 'number')}
  previewImage={previewImage}
  pushToast={pushToast}
  imageRef={imageRef}
  textColors={textColors}
  kitPrices={kitPrices}
  previewTextPositions={previewTextPositions}
  fontsLoaded={fontsLoaded}  // Add this if missing
  isMobile={isMobile}        // Add this if missing
  availableRows={availableSeatViews}
        
      />
    );
  }

  if (isPreviewOpen) {
    return (
      <PreviewPage
        savedImages={savedImages}
        availableRows={availableSeatViews}
        onModify={() => {
          setIsPreviewOpen(false);
          if (primarySeatView) {
            setSelectedSeatView(primarySeatView);
          } else {
            setSelectedSeatView(lastEditedRow);
          }
          setForceFlowAfterModify(true);
        }}
        onConfirm={() => {
          setIsPreviewOpen(false);
          setShowOrderForm(true);
        }}
        onClose={() => setIsPreviewOpen(false)}
      />
    );
  }

  return (
    <div className="app-container">
      {preloadSelectedFont()}
      
      {/* SECURITY: Enhanced loading screen with validation */}
      {isPreviewLoading && (
        <div className="mgl" role="status" aria-live="polite">
          <div className="mgl__inner">
            {/* Stitch line - a thread being sewn, rather than a generic
                spinner. Reads as the craft the product is about. */}
            <div className="mgl__stitch" aria-hidden="true">
              <span className="mgl__thread" />
              <span className="mgl__needle" />
            </div>

            <p className="mgl__title">Preparing your preview</p>
            <p className="mgl__sub">Applying your personalisation to both rows</p>
          </div>
        </div>
      )}

      <div className="mgp-layout">
        {/* ---------- Configurator ---------- */}
        <section className="mgp-config">
          <header className="mgp-config__intro">
            <span className="mgp-eyebrow">Personalisation</span>
            <h1 className="mgp-title">Your drive. Your space.</h1>
            <p className="mgp-lede">
              Crafted details, finished to suit your style.
            </p>
          </header>

          <div className="mgp-fields">
            {/* Vehicle model - floating label select */}
            <div className="mgp-field">
              <MgSelect
                id="mgp-model"
                label="Vehicle Model"
                value={selectedVehicleModel}
                options={vehicleModels}
                onChange={handleVehicleModelChange}
              />
            </div>

            {/* Accessory type */}
            <div className="mgp-field">
              <MgSelect
                id="mgp-kit"
                label="Accessory Type"
                value={selectedAccessory}
                options={accessories}
                onChange={handleAccessoryChange}
                disabled={!selectedVehicleModel}
              />
            </div>

            <div className="mgp-field">
              <span className="mgp-legend">Personalisation Type</span>
              <div className="mgp-mode" role="tablist" aria-label="Personalisation type">
                <span
                  className={`mgp-mode__thumb${personalisationMode === 'design' ? ' is-design' : ''}`}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  role="tab"
                  aria-selected={personalisationMode === 'text'}
                  className={`mgp-mode__btn${personalisationMode === 'text' ? ' is-active' : ''}`}
                  onClick={() => handlePersonalisationModeChange('text')}
                >
                  Text
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={personalisationMode === 'design'}
                  className={`mgp-mode__btn${personalisationMode === 'design' ? ' is-active' : ''}`}
                  onClick={() => handlePersonalisationModeChange('design')}
                >
                  Design
                </button>
              </div>
            </div>

            {personalisationMode === 'text' ? (
              <>
                <div className="mgp-field">
                  <div className={`mgp-control mgp-control--text${personalisedContent ? ' has-value' : ''}`}>
                    <input
                      id="mgp-text"
                      className="mgp-control__input"
                      type="text"
                      maxLength={7}
                      value={personalisedContent}
                      onChange={handlePersonalisedContentChange}
                      style={{
                        fontFamily: selectedFont
                          ? `"${validateInput(selectedFont, 'font')}", serif`
                          : undefined,
                        color: selectedColor || undefined,
                      }}
                    />
                    <label className="mgp-control__label" htmlFor="mgp-text">
                      Your Text
                    </label>
                    <span className="mgp-control__meter" aria-hidden="true">
                      {personalisedContent.length}/7
                    </span>
                  </div>
                </div>

                <div className="mgp-field">
                  <MgSelect
                    id="mgp-font"
                    label="Font Style"
                    value={selectedFont || ''}
                    options={fontStyles}
                    onChange={handleFontChange}
                    optionStyle={(f) => ({ fontFamily: `"${f}", serif` })}
                  />
                </div>

                <div className="mgp-field">
                  <span className="mgp-legend">
                    Thread Colour
                    {selectedColor && (
                      <em>{textColors.find(c => c.value === selectedColor)?.name}</em>
                    )}
                  </span>
                  <div className="mgp-swatches">
                    {textColors.map(color => (
                      <button
                        key={color.value}
                        type="button"
                        className={`mgp-swatch${selectedColor === color.value ? ' is-selected' : ''}`}
                        style={{ backgroundColor: color.value }}
                        onClick={() => handleColorChange(color.value)}
                        title={color.name}
                        aria-label={color.name}
                        aria-pressed={selectedColor === color.value}
                      />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="mgp-field">
                <span className="mgp-legend">
                  Design Palette
                  {selectedDesign && <em>{selectedDesign.name}</em>}
                </span>
                <div className="mgp-design-palette" role="radiogroup" aria-label="Design palette">
                  {availableDesigns.map(design => (
                  <button
                    key={design.id}
                    type="button"
                    role="radio"
                    aria-checked={selectedDesignId === design.id}
                    className={`mgp-design-option${selectedDesignId === design.id ? ' is-selected' : ''}`}
                    onClick={() => handleDesignChange(design.id)}
                    title={design.name}
                  >
                    <DesignArtwork design={design} className="mgp-design-option__art" />
                    <span className="mgp-design-option__name">{design.name}</span>
                    <span className="mgp-design-option__check" aria-hidden="true" />
                    {isCustomDesignId(design.id) && (
                      <span
                        className="mgp-design-option__remove"
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${design.name}`}
                        title={`Remove ${design.name}`}
                        onClick={(event) => { event.stopPropagation(); removeCustomDesign(design.id); }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          event.stopPropagation();
                          removeCustomDesign(design.id);
                        }}
                      >
                        <X size={11} />
                      </span>
                    )}
                  </button>
                  ))}

                  {customDesigns.length < CUSTOM_DESIGN_MAX_SLOTS && (
                    <button
                      type="button"
                      className="mgp-design-option mgp-design-option--upload"
                      onClick={() => setShowCustomDesignNotice(true)}
                      disabled={isProcessingCustomDesign}
                      title="Upload your own design"
                    >
                      <span className="mgp-design-option__art mgp-design-upload-mark" aria-hidden="true">
                        <Plus size={18} />
                      </span>
                      <span className="mgp-design-option__name">Upload</span>
                    </button>
                  )}

                  <input
                    ref={customDesignInputRef}
                    type="file"
                    accept={CUSTOM_DESIGN_ACCEPT}
                    onChange={handleCustomDesignUpload}
                    multiple
                    hidden
                  />
                </div>
                {customDesignError && (
                  <small className="mgp-design-palette__error" role="alert">{customDesignError}</small>
                )}
              </div>
            )}
          </div>

          {/* Quantity + price */}
          <div className="mgp-summary">
            <div className="mgp-qty">
              <span className="mgp-legend">No. of Sets</span>
              <div className="mgp-stepper">
                <button
                  type="button"
                  onClick={() => setNumSets(prev => Math.max(1, prev - 1))}
                  aria-label="Decrease quantity"
                >&#8722;</button>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={numSets}
                  onChange={handleNumSetsChange}
                  className="no-spinner"
                  aria-label="Number of sets"
                />
                <button
                  type="button"
                  onClick={() => setNumSets(prev => Math.min(100, prev + 1))}
                  aria-label="Increase quantity"
                >+</button>
              </div>
            </div>

            <div className="mgp-price">
              <span className="mgp-price__label">Total MRP</span>
              <span className="mgp-price__value">
                {selectedAccessory && kitPrices[selectedAccessory]
                  ? PRICE_PLACEHOLDER
                  : '--'}
              </span>
              {selectedAccessory && (
                <span className="mgp-price__note">Inclusive of all taxes</span>
              )}
            </div>
          </div>

          <button
            className="mgp-cta"
            onClick={handlePreviewClick}
            title="Preview both rows"
          >
            <span>Preview Personalisation</span>
            <span className="mgp-cta__arrow" aria-hidden="true" />
          </button>
        </section>

        {/* ---------- Preview stage ---------- */}
        <section className="mgp-stage">
          <div className="mgp-stage__frame">
            {selectedVehicleModel && selectedAccessory && (
              <button
                type="button"
                className={`mgp-adjust-toggle${isAdjustMode ? ' is-active' : ''}`}
                onClick={() => setIsAdjustMode(current => !current)}
                aria-label="Toggle placement tool"
                aria-pressed={isAdjustMode}
                title="Placement tool"
              >
                <SlidersHorizontal size={17} strokeWidth={1.8} />
              </button>
            )}

            {isAdjustMode && activePlacement && (
              <aside
                ref={adjustPanelRef}
                className={`mgp-adjust${isAdjustDragging ? ' is-dragging' : ''}`}
                aria-label="Placement tool"
                style={adjustPanelOffset ? {
                  left: `${adjustPanelOffset.x}px`,
                  top: `${adjustPanelOffset.y}px`,
                  right: 'auto',
                } : undefined}
              >
                <header
                  className="mgp-adjust__head"
                  onPointerDown={startAdjustDrag}
                  onPointerMove={moveAdjustDrag}
                  onPointerUp={endAdjustDrag}
                  onPointerCancel={endAdjustDrag}
                  title="Drag to move"
                >
                  <div>
                    <span>Placement Tool</span>
                    <small>{personalisationMode === 'design' ? 'Design' : 'Text'}</small>
                  </div>
                  <button
                    type="button"
                    className="mgp-icon-btn"
                    onClick={() => setIsAdjustMode(false)}
                    aria-label="Close placement tool"
                    title="Close"
                  >
                    <X size={16} />
                  </button>
                </header>

                <div className="mgp-adjust__context">
                  <strong>{selectedAccessory}</strong>
                  <span>{selectedSeatView}</span>
                </div>

                <div className="mgp-adjust__targets" role="tablist" aria-label="Product target">
                  {adjustablePositions.map((_, index) => (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activePlacementIndex === index}
                      className={activePlacementIndex === index ? 'is-active' : ''}
                      onClick={() => setActivePlacementIndex(index)}
                      key={index}
                    >
                      {index === 0 ? 'Left Product' : `Right Product ${index > 1 ? index : ''}`}
                    </button>
                  ))}
                </div>

                <div className="mgp-adjust__coordinates">
                  <label>
                    <span>Top</span>
                    <div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step={placementStep}
                        value={Number.parseFloat(activePlacement.top) || 0}
                        onChange={(event) => setPlacementCoordinate('top', event.target.value)}
                      />
                      <small>%</small>
                    </div>
                  </label>
                  <label>
                    <span>Left</span>
                    <div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step={placementStep}
                        value={Number.parseFloat(activePlacement.left) || 0}
                        onChange={(event) => setPlacementCoordinate('left', event.target.value)}
                      />
                      <small>%</small>
                    </div>
                  </label>
                  <label>
                    <span>Right</span>
                    <div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step={placementStep}
                        value={Number((100 - (Number.parseFloat(activePlacement.left) || 0)).toFixed(2))}
                        onChange={(event) => setPlacementCoordinate('left', 100 - Number(event.target.value))}
                      />
                      <small>%</small>
                    </div>
                  </label>
                  <label>
                    <span>Bottom</span>
                    <div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step={placementStep}
                        value={Number((100 - (Number.parseFloat(activePlacement.top) || 0)).toFixed(2))}
                        onChange={(event) => setPlacementCoordinate('top', 100 - Number(event.target.value))}
                      />
                      <small>%</small>
                    </div>
                  </label>
                </div>

                <div className="mgp-adjust__workbench">
                  <div className="mgp-adjust__dpad" aria-label="Nudge position">
                    <button type="button" onClick={() => nudgePlacement(0, -1)} aria-label="Move up" title="Move up">
                      <ChevronUp size={17} />
                    </button>
                    <button type="button" onClick={() => nudgePlacement(-1, 0)} aria-label="Move left" title="Move left">
                      <ChevronLeft size={17} />
                    </button>
                    <span aria-hidden="true" />
                    <button type="button" onClick={() => nudgePlacement(1, 0)} aria-label="Move right" title="Move right">
                      <ChevronRight size={17} />
                    </button>
                    <button type="button" onClick={() => nudgePlacement(0, 1)} aria-label="Move down" title="Move down">
                      <ChevronDown size={17} />
                    </button>
                  </div>

                  <div className="mgp-adjust__size">
                    <span>{personalisationMode === 'design' ? 'Design Size' : 'Font Size'}</span>
                    <div>
                      <button type="button" onClick={() => resizePlacement(-1)} aria-label="Decrease size" title="Decrease size">
                        <Minus size={15} />
                      </button>
                      <input
                        type="number"
                        min={personalisationMode === 'design' ? 8 : 4}
                        max={personalisationMode === 'design' ? 120 : 72}
                        value={Number(activePlacementSize.toFixed(1))}
                        onChange={(event) => setPlacementSize(event.target.value)}
                        aria-label={personalisationMode === 'design' ? 'Design size' : 'Font size'}
                      />
                      <button type="button" onClick={() => resizePlacement(1)} aria-label="Increase size" title="Increase size">
                        <Plus size={15} />
                      </button>
                    </div>
                    <small>px</small>
                  </div>
                </div>

                <div className="mgp-adjust__step">
                  <span>Nudge</span>
                  {[0.1, 0.5, 1].map(step => (
                    <button
                      type="button"
                      key={step}
                      className={placementStep === step ? 'is-active' : ''}
                      onClick={() => setPlacementStep(step)}
                    >
                      {step}%
                    </button>
                  ))}
                </div>

                <footer className="mgp-adjust__actions">
                  <button type="button" onClick={resetPlacementTool}>
                    <RotateCcw size={15} />
                    <span>Reset</span>
                  </button>
                  <button type="button" className="is-primary" onClick={copyPlacementCoordinates}>
                    <Copy size={15} />
                    <span>Copy Coordinates</span>
                  </button>
                </footer>
              </aside>
            )}

            <div className="image-container" ref={imageRef}>
              {isImageLoading && (
                <div className="mgp-stage__loader">
                  <span className="mgp-spinner" aria-hidden="true" />
                  <span>Loading preview</span>
                </div>
              )}

              <img
                src={getImagePath()}
                alt="Accessory Preview"
                className="headrest-image"
                style={{ display: isImageLoading ? 'none' : 'block' }}
                onLoad={() => setIsImageLoading(false)}
                onError={(e) => {
                  console.warn('Failed to load image:', e.target.src);
                  setIsImageLoading(false);
                  e.target.src = '/Inside Page Banner.png';
                }}
                onLoadStart={() => setIsImageLoading(true)}
              />

              {personalisationMode === 'text' &&
               personalisedContent &&
               validateInput(selectedVehicleModel, 'vehicleModel') &&
               validateInput(selectedAccessory, 'accessory') &&
               selectedSeatView &&
               fontsLoaded && (
                <>
                  {(isAdjustMode
                    ? adjustablePositions
                    : textPositions[selectedVehicleModel]?.[selectedSeatView]?.[selectedAccessory] || []
                  ).map((position, index) => (
                    position ? (
                      <EmbroideredText
                        key={`${index}-${selectedColor}-${selectedFont}`}
                        text={validateInput(personalisedContent, 'personalised')}
                        fontFamily={validateInput(selectedFont, 'font')}
                        position={position}
                        textColor={validateInput(selectedColor, 'color')}
                        isMobile={isMobile}
                        isAdjustSelected={isAdjustMode && activePlacementIndex === index}
                      />
                    ) : null
                  ))}
                </>
              )}

              {personalisationMode === 'design' &&
               selectedDesign &&
               validateInput(selectedVehicleModel, 'vehicleModel') &&
               validateInput(selectedAccessory, 'accessory') &&
               selectedSeatView && (
                <>
                  {(isAdjustMode
                    ? adjustablePositions
                    : getDesignPositions(
                        textPositions,
                        selectedVehicleModel,
                        selectedSeatView,
                        selectedAccessory
                      )
                  ).map((position, index) => (
                    position ? (
                      <EmbroideredDesign
                        key={`${selectedDesign.id}-${index}`}
                        design={selectedDesign}
                        position={position}
                        isMobile={isMobile}
                        placementScale={getDesignPlacementScale(selectedAccessory, selectedSeatView)}
                        isAdjustSelected={isAdjustMode && activePlacementIndex === index}
                      />
                    ) : null
                  ))}
                </>
              )}
            </div>

            {/* Floats over the image so the preview can use the full
                column height rather than giving up a row beneath it. */}
            {(validateInput(selectedVehicleModel, 'vehicleModel') &&
              validateInput(selectedAccessory, 'accessory') &&
              availableSeatViews.length > 1) && (
              <div className="mgp-rowswitch" role="tablist" aria-label="Seat row">
                <span
                  className={`mgp-rowswitch__thumb${selectedSeatView === 'Rear Row' ? ' is-rear' : ''}`}
                  aria-hidden="true"
                />
                <button
                  role="tab"
                  aria-selected={selectedSeatView === 'Front Row'}
                  className={`mgp-rowswitch__btn${selectedSeatView === 'Front Row' ? ' is-active' : ''}`}
                  onClick={() => handleSeatViewChange('Front Row')}
                >
                  Front
                </button>
                <button
                  role="tab"
                  aria-selected={selectedSeatView === 'Rear Row'}
                  className={`mgp-rowswitch__btn${selectedSeatView === 'Rear Row' ? ' is-active' : ''}`}
                  onClick={() => handleSeatViewChange('Rear Row')}
                  disabled={!availableSeatViews.includes('Rear Row')}
                  title={availableSeatViews.includes('Rear Row') ? 'Rear row' : 'Not available for this kit'}
                >
                  Rear
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* SECURITY: Enhanced popup dialogs with validation */}
      {showSuccessPopup && (
        <div className="mgm-overlay" role="alertdialog" aria-modal="true">
          <div className="mgm mgm--alert">
            <div className="mgm-alert">
              <span className="mgm-alert__mark mgm-alert__mark--ok" aria-hidden="true" />
              <h3 className="mgm-alert__title">Download complete</h3>
              <p className="mgm-alert__text">
                {validateInput(downloadedFileName, 'text')} has been saved to your device.
              </p>
            </div>
            <footer className="mgm__foot mgm__foot--center">
              <button
                type="button"
                className="mgm-btn mgm-btn--primary"
                onClick={() => setShowSuccessPopup(false)}
                autoFocus
              >
                Done
              </button>
            </footer>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="mgm-overlay" role="alertdialog" aria-modal="true">
          <div className="mgm mgm--alert">
            <div className="mgm-alert">
              <span className="mgm-alert__mark mgm-alert__mark--info" aria-hidden="true" />
              <h3 className="mgm-alert__title">Almost there</h3>
              <p className="mgm-alert__text">
                Choose a vehicle model, accessory type and add your text before saving.
              </p>
            </div>
            <footer className="mgm__foot mgm__foot--center">
              <button
                type="button"
                className="mgm-btn mgm-btn--primary"
                onClick={() => setShowPopup(false)}
                autoFocus
              >
                Got it
              </button>
            </footer>
          </div>
        </div>
      )}

      {isProcessingCustomDesign && (
        <div className="mgm-overlay" role="alertdialog" aria-modal="true">
          <div className="mgm mgm--progress">
            <div className="mgm-progress">
              <div className="mgm-progress__pulse" aria-hidden="true">
                <span /><span /><span />
              </div>

              <h3 className="mgm-progress__title">
                {customDesignProgress.step === 'download'
                  ? 'Preparing the cut-out tool'
                  : 'Getting it stitch-ready'}
              </h3>

              <p className="mgm-progress__text">
                {customDesignProgress.step === 'download'
                  ? `One-time download, ${Math.round((customDesignProgress.ratio || 0) * 100)}% complete.`
                  : 'Cleaning up the edges so it can be embroidered.'}
              </p>

              {customDesignProgress.total > 1 && (
                <span className="mgm-progress__count">
                  Image {Math.min(customDesignProgress.index + 1, customDesignProgress.total)} of {customDesignProgress.total}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {showCustomDesignNotice && (
        <div className="mgm-overlay" role="alertdialog" aria-modal="true">
          <div className="mgm mgm--upload">
            <div className="mgm-upload">
              <span className="mgm-upload__badge">
                <Sparkles size={13} strokeWidth={2} aria-hidden="true" />
                Custom design
              </span>

              <h3 className="mgm-upload__title">Before you upload</h3>

              <p className="mgm-upload__text">
                The design you upload will be reviewed by our embroidery design
                team to confirm the feasibility of exact replication.
                Confirmation will be shared with you within 36 hours along with
                the representative image of the feasible design.
              </p>

              <div className="mgm-upload__meta">
                <span className="mgm-upload__meta-item">
                  <Clock size={14} strokeWidth={1.9} aria-hidden="true" />
                  Reviewed within 36 hours
                </span>
                <span className="mgm-upload__meta-item">
                  <ImageIcon size={14} strokeWidth={1.9} aria-hidden="true" />
                  Preview image shared
                </span>
              </div>

              <div className="mgm-upload__actions">
                <button
                  type="button"
                  className="mgm-btn mgm-btn--primary mgm-upload__cta"
                  onClick={() => {
                    setShowCustomDesignNotice(false);
                    customDesignInputRef.current?.click();
                  }}
                  autoFocus
                >
                  Choose Image
                </button>
                <button
                  type="button"
                  className="mgm-upload__cancel"
                  onClick={() => setShowCustomDesignNotice(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {actionPopup.open && (
        <div className="mgm-overlay" role="alertdialog" aria-modal="true">
          <div className="mgm mgm--alert">
            <div className="mgm-alert">
              <span className="mgm-alert__mark mgm-alert__mark--info" aria-hidden="true" />
              <p className="mgm-alert__text mgm-alert__text--lead">
                {validateInput(actionPopup.message, 'text')}
              </p>
            </div>
            <footer className="mgm__foot mgm__foot--center">
              <button
                type="button"
                className="mgm-btn mgm-btn--primary"
                onClick={() => setActionPopup({ open: false, message: '' })}
                autoFocus
              >
                Got it
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* SECURITY: Enhanced Toast notifications */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          zIndex: 3000
        }}>
          {toasts.map(t => (
            <div key={t.id} className="toast-card" style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              minWidth: '300px',
              maxWidth: '460px',
              padding: '14px 16px',
              borderRadius: '14px',
              backgroundColor: '#ffffff',
              border: '1px solid #EEE',
              boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
              color: '#111',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                background: t.variant === 'error' 
                  ? 'linear-gradient(180deg, #e11d2e 0%, #b3121c 100%)'
                  : 'linear-gradient(180deg, #1aa851 0%, #0f7f3a 100%)'
              }} />

              <div style={{
                width: '10px',
                height: '10px',
                marginTop: '4px',
                borderRadius: '50%',
                backgroundColor: t.variant === 'error' ? '#e11d2e' : '#1aa851'
              }} />

              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', color: '#374151' }}>
                  {validateInput(t.message, 'text')}
                </div>
              </div>

              <button
                className="toast-close"
                onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                aria-label="Dismiss notification"
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent',
                  color: '6b7280',
                  fontSize: '16px',
                  lineHeight: 1,
                  padding: '4px',
                  position: 'absolute',
                  top: '8px',
                  right: '10px'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default App;
